import boDatatableMixin from "@/v2/mixins/bo-datatable.js";
import cartMixin from "@/v2/mixins/carts.js";
import couponsMixin from "@/v2/mixins/coupons.js";
import messageBagMixin from "@/v2/mixins/message-bag.js";
import productMixin from "@/v2/mixins/products.js";
import shippingMethodCalculationsMixin from "@/v2/mixins/shipping-method-calculations.js";
import shippingZoneMixin from "@/v2/mixins/shipping-zones.js";
import warehouseInventoryMixin from "@/v2/mixins/warehouse-inventories.js";
import warehouseMixin from "@/v2/mixins/warehouses.js";
import { Prompt } from "@/v2/models/bo-confirmation.js";
import { constructSearchListItems, searchProperties } from "@/v2/models/bo-dropdown-search.js";
import { BundleProduct, VariableProduct } from "@/v2/models/products";
import { keyBy } from "@/v2/utils/array";
import { dateDayDifference } from "@/v2/utils/date";
import * as utils from "@/v2/utils/utilities.js";
import moment from "moment";
import Vue from "vue";
import {
    priceVisibilityMixin,
    filterCatalogPriceColumns,
    resolveCanReadPrices,
} from "js/frontend/mixins/price-visibility.js";
import {
    filterItemsByLabels,
    getAvailableCartLabels,
    getCartItemLabelIds,
} from "js/frontend/utils/cart-item-labels.js";
import { cartItemDiscountSteps } from "js/frontend/utils/line-discount-label.js";
import cartStockMixin from "js/frontend/mixins/cart-stock.js";

Vue.component("bo-textarea-word-count", require("@/bo-textarea-word-count.vue").default);
// Vue.component("bo-cart-list", require("js/frontend/components/bo-cart-list.vue").default);
Vue.component("bo-panel", require("js/frontend/components/bo-panel.vue").default);
Vue.component("bo-tab", require("@/v2/components/bo-tab.vue").default);
Vue.component("bo-cell-currency", require("@/v2/components/bo-cell-currency.vue").default);
Vue.component("bo-dropdown-search", require("@/v2/components/bo-dropdown-search.vue").default);
Vue.component("bo-confirmation", require("@/v2/components/bo-confirmation.vue").default);
Vue.component("bo-cell-number", require("@/v2/components/bo-cell-number.vue").default);
Vue.component("bo-popup-modal", require("@/v2/components/bo-popup-modal.vue").default);
Vue.component("bo-datatable", require("@/v2/components/bo-datatable.vue").default);
Vue.component("bo-cell-datetime", require("@/v2/components/bo-cell-datetime.vue").default);
Vue.component("bo-attachment", require("@/v2/components/bo-attachment.vue").default);
Vue.component("bo-textarea-word-count", require("@/v2/components/bo-textarea-word-count.vue").default);
Vue.component("setting-select_multiple", require("@/v2/components/settings/setting-select_multiple.vue").default);
Vue.component("bo-dropdown-search", require("@/v2/components/bo-dropdown-search.vue").default);
Vue.component("bo-shipping-method-select", require("@/v2/components/bo-shipping-method-select.vue").default);
Vue.component("bo-empty-cart", require("@/v2/components/bo-empty-cart.vue").default);
Vue.component("bo-coupon-list", require("js/frontend/components/bo-coupon-list.vue").default);
Vue.component("bo-cell-button-popup-modal", require("@/v2/components/bo-cell-button-popup-modal.vue").default);
Vue.component("bo-cell-button-popup", require("@/v2/components/bo-cell-button-popup.vue").default);
Vue.component("bo-select", require("@/v2/components/bo-select.vue").default);
Vue.component("bo-new-shipping-address", require("@/v2/components/customers/bo-new-shipping-address.vue").default);
Vue.component("bo-cart-list", require("@/v2/components/bo-cart-list.vue").default);
Vue.component("bo-line-discount", require("js/frontend/components/bo-line-discount.vue").default);
Vue.component("bo-label-filter", require("js/frontend/components/bo-label-filter.vue").default);

const app = new Vue({
    el: "#app",
    mixins: [
        cartMixin,
        messageBagMixin,
        couponsMixin,
        warehouseMixin,
        warehouseInventoryMixin,
        shippingZoneMixin,
        boDatatableMixin,
        shippingMethodCalculationsMixin,
        productMixin,
        priceVisibilityMixin,
        cartStockMixin,
    ],
    data() {
        let shippingAddressCustomFields = {};
        let shippingAddressCustomFieldValues = {};
        window.bo.shippingAddressCustomFields.forEach((field) => {
            if (field.type === "select") {
                let options = {};
                field.options.forEach((option) => {
                    options[option.value] = option;
                });
                field.options = options;
            }

            shippingAddressCustomFields[field.slug] = field;
            shippingAddressCustomFieldValues[field.slug] = "";
        });

        let deliveryAddresses = window.bo.deliveryAddresses.map((address) => {
            if (address.custom_fields) {
                address.custom_fields = keyBy(address.custom_fields, "slug");
            }
            return address;
        });

        let quantityColumn = window.bo.enableQuotationTransferToSalesOrder
            ? {
                  id: "quantity",
                  label: window.bo.cartTranslations.request,
                  type: "number",
                  orderable: true,
                  searchable: false,
                  displayMax: true,
                  editable: true,
                  min: 1,
                  decimal: window.bo.lineItemMaxDecimalPlace,
                  maxTransformer: (record) => {
                      return record.line_item?.untransferred_quantity;
                  },
              }
            : {
                  id: "quantity",
                  label: window.bo.cartTranslations.quantity,
                  type: "number",
                  orderable: true,
                  searchable: false,
                  editable: true,
                  min: 1,
                  decimal: window.bo.lineItemMaxDecimalPlace,
                  transformer: (record) => {
                      let quantity = parseFloat(record.quantity);

                      if (record._type == "child_bundle_cart_item") {
                          return quantity * record.composite_parent_quantity;
                      } else {
                          return quantity;
                      }
                  },
              };

        // labels the cart can show on this site: the column and its filter are left out when there are none
        const productLabelOptions = getAvailableCartLabels(
            { canReadPrices: resolveCanReadPrices(window.bo), hasInventory: !!window.bo.hasInventory },
            window.bo.cartTranslations
        );

        const showLineItemShippingAddress = window.bo.showLineItemShippingAddress;
        let columns = [
            {
                id: "id",
                label: window.bo.cartTranslations.cart_item_id,
                disabled: true,
                type: "text",
                primary_key: true,
            },
            {
                id: "document_number",
                label: window.bo.cartTranslations.doc_number,
                type: "text",
                orderable: true,
                searchable: true,
                disabled: !window.bo.enableQuotationTransferToSalesOrder,
            },
            {
                id: "product_sku",
                label: window.bo.cartTranslations.sku,
                type: "text",
                transformer: (record) => {
                    if (record.product instanceof VariableProduct) {
                        return record.product?.variations[record.product_id]?.sku;
                    } else if (record.product instanceof BundleProduct) {
                        return record.product?.sku;
                    }
                },
                orderable: true,
                searchable: true,
            },
            {
                id: "product_name",
                label: window.bo.cartTranslations.product_name,
                type: "text",
                transformer: (record) => {
                    if (record.product instanceof VariableProduct) {
                        const variationName = record.product?.variations?.[record.product_id]?.name;
                        return variationName && variationName.trim() !== "" ? variationName : record.product?.name;
                    } else if (record.product instanceof BundleProduct) {
                        return record.product?.name;
                    }
                },
                orderable: true,
                searchable: true,
                editable: false,
            },
            {
                id: "uom",
                label: window.bo.cartTranslations.uom,
                type: "text",
                transformer: (record) => {
                    if (record.product instanceof VariableProduct) {
                        return record?.product.getSelectedUom().name;
                    }

                    return null;
                },
                orderable: true,
                searchable: true,
                editable: false,
            },
            {
                id: "product_labels",
                label: window.bo.cartTranslations.product_labels,
                type: "custom",
                orderable: false,
                searchable: false,
                editable: false,
                disabled: productLabelOptions.length === 0,
            },
            {
                id: "packsize",
                label: window.bo.cartTranslations.packsize ?? "Pack Size",
                type: "text",
                transformer: (record) => {
                    const packsize = record?.product?.getPacksize?.() ?? record?.product?.packsize;
                    return packsize && packsize.trim() !== "" ? packsize : "-";
                },
                orderable: true,
                searchable: true,
                editable: false,
            },
            {
                id: "shipping_class",
                label: window.bo.cartTranslations.shipping_class,
                type: "text",
                transformer: (record) => {
                    if (record.product instanceof VariableProduct) {
                        return record?.product?.getSelectedVariation()?.shipping_class?.name;
                    }

                    return null;
                },
                orderable: true,
                searchable: true,
                editable: false,
            },
            {
                label: window.bo.cartTranslations.delivery_address,
                id: "shipping_address",
                orderable: false,
                visibility: true,
                type: "custom",
                search: "",
                searchable: false,
                editable: true,
            },
            {
                id: "stock",
                label:
                    window.bo.hasCapabilityPhysicalStocks || window.bo.hasCapabilitySellableStocks
                        ? window.bo.cartTranslations.sellable_stock
                        : window.bo.cartTranslations.stock,
                type: "text",
                editable: false,
                searchable: false,
                disabled: !window.bo.hasInventory,
                transformer: (record) => {
                    // if no warehouse for user and HQ
                    if (!window.bo.hasInventory) {
                        return "";
                    }

                    if (record._type == "bundle_cart_item") {
                        let isStockSufficient = true;
                        const allow_backorders = this.getProductAllowBackorder(record.product);
                        const disallow_children_backorders = record.product.disallow_children_backorders; // bundle disallow_children_backorders

                        const bundleBalance = this.warehouseInventories[record.product.id]?.balance ?? 0;
                        if (bundleBalance < record.product.quantity && !allow_backorders) {
                            isStockSufficient = false;
                        }

                        Object.values(record.composite_children).forEach((child) => {
                            if (typeof this.bundleCartItemError[record.id] == "undefined") {
                                this.bundleCartItemError[record.id] = {};
                            }
                            if (typeof this.bundleCartItemError[record.id][child.component_id] == "undefined") {
                                this.bundleCartItemError[record.id][child.component_id] = {};
                            }
                            if (
                                typeof this.bundleCartItemError[record.id][child.component_id][child.product_id] ==
                                "undefined"
                            ) {
                                this.bundleCartItemError[record.id][child.component_id][child.product_id] = {};
                            }

                            // In Stock / Out of Stock tag for bundle component
                            let sellableBalance = this.warehouseInventories[child.product_id]?.balance ?? 0;
                            if (sellableBalance < child.quantity) {
                                this.bundleCartItemError[record.id][child.component_id][child.product_id][
                                    "in_stock_status"
                                ] = false;
                            } else {
                                this.bundleCartItemError[record.id][child.component_id][child.product_id][
                                    "in_stock_status"
                                ] = true;
                            }

                            // Backorder tag for bundle component
                            const variation_allow_backorders =
                                child["product"]?.["variations"]?.[child.product_id]?.allow_backorders ?? false; // variation allow_backorders
                            if (variation_allow_backorders && !disallow_children_backorders) {
                                this.bundleCartItemError[record.id][child.component_id][child.product_id][
                                    "allow_backorder"
                                ] = true;
                            } else {
                                this.bundleCartItemError[record.id][child.component_id][child.product_id][
                                    "allow_backorder"
                                ] = false;

                                if (sellableBalance < child.quantity) {
                                    isStockSufficient = false;
                                }
                            }
                        });

                        if (isStockSufficient) {
                            return window.bo.cartTranslations.in_stock;
                        } else {
                            return window.bo.cartTranslations.out_of_stock;
                        }
                    } else {
                        if (this.warehouseInventories[record.product_id] == null) {
                            // User has capabilities and no stock
                            // only for rough sellable stocks
                            if (
                                window.bo.hasCapabilityRoughSellableStocks &&
                                !window.bo.hasCapabilityPhysicalStocks &&
                                !window.bo.hasCapabilitySellableStocks
                            ) {
                                return window.bo.roughStockText;
                            } else if (
                                !window.bo.hasCapabilityPhysicalStocks &&
                                window.bo.hasCapabilitySellableStocks
                            ) {
                                return window.bo.cartTranslations.sellable;
                            } else if (window.bo.hasCapabilityPhysicalStocks) {
                                return 0;
                            } else {
                                // User has no capabilities and no stock
                                return window.bo.cartTranslations.out_of_stock;
                            }
                        } else {
                            // User has capabilities but has stock
                            const sellableBalance = this.warehouseInventories[record.product_id].balance;
                            // only for rough sellable stocks
                            if (
                                window.bo.hasCapabilityRoughSellableStocks &&
                                !window.bo.hasCapabilityPhysicalStocks &&
                                !window.bo.hasCapabilitySellableStocks
                            ) {
                                return this.warehouseInventories[record.product_id].stock_balance;
                            } else if (
                                !window.bo.hasCapabilityPhysicalStocks &&
                                window.bo.hasCapabilitySellableStocks
                            ) {
                                return this.warehouseInventories[record.product_id].stock_balance;
                            } else if (window.bo.hasCapabilityPhysicalStocks) {
                                return this.warehouseInventories[record.product_id].balance.toFixed(2);
                            } else {
                                // User has no capabilities and has stock
                                if (sellableBalance > 0) {
                                    return window.bo.cartTranslations.in_stock;
                                } else {
                                    return window.bo.cartTranslations.out_of_stock;
                                }
                            }
                        }
                    }
                },
            },
            {
                id: "backorder",
                label: window.bo.cartTranslations.backorder,
                type: "text",
                editable: false,
                searchable: false,
                disabled: !window.bo.hasInventory,
                transformer: (record) => {
                    // if no warehouse for user and HQ
                    if (!window.bo.hasInventory) {
                        return "";
                    }

                    if (record.product.type == "bundle") {
                        return "";
                    }

                    const allow_backorders = this.getProductAllowBackorder(record.product);

                    if (allow_backorders) {
                        return window.bo.cartTranslations.yes;
                    } else {
                        return "-";
                    }
                },
            },
            {
                id: "physical_stock",
                label: window.bo.cartTranslations.physical_stock,
                type: "text",
                editable: false,
                searchable: false,
                disabled: !window.bo.hasCapabilityPhysicalStocks,
                transformer: (record) => {
                    if (this.warehouseInventories[record.product_id] == null) {
                        return 0;
                    } else {
                        return this.warehouseInventories[record.product_id].physical_balance.toFixed(2);
                    }
                },
            },
            {
                id: "price",
                label: window.bo.cartTranslations.price,
                type: "number",
                editable: false,
                searchable: false,
                decimal: window.bo.lineItemUnitPriceMaxDecimalPlace,
                transformer: (record) => {
                    if (record._type == "bundle_cart_item") {
                        return record.getDisplayRegularPrice();
                    } else {
                        return record.getOriginalPrice();
                    }
                },
            },
            {
                id: "tax_name",
                label: window.bo.cartTranslations.tax_name,
                type: "text",
                editable: false,
                transformer: (record) => {
                    if (this.isTaxExempted || !record.product.tax_class?.visible) {
                        return null;
                    }

                    if (window.bo.enableQuotationTransferToSalesOrder) {
                        return record.line_item?.tax_class?.name;
                    }
                    return record.product.tax_class?.name;
                },
            },
            {
                id: "tax_percentage",
                label: window.bo.cartTranslations.tax_percentage,
                type: "text",
                editable: false,
                searchable: false,
                transformer: (record) => {
                    if (this.isTaxExempted || !record.product.tax_class?.visible) {
                        return null;
                    }

                    if (window.bo.enableQuotationTransferToSalesOrder) {
                        return record.line_item?.tax_class ? record.line_item.tax_class.percentage + "%" : "";
                    }
                    return record.product.tax_class ? record.product.tax_class.percentage + "%" : "";
                },
            },
            {
                id: "lead_time",
                label: window.bo.cartTranslations.lead_time,
                type: "number",
                editable: false,
                orderable: true,
                searchable: false,
                transformer: (record) => record.product.lead_time,
            },
            {
                id: "line_item_quantity",
                label: window.bo.cartTranslations.quantity,
                type: "number",
                orderable: true,
                searchable: false,
                transformer: (record) => record.line_item?.quantity,
                disabled: !window.bo.enableQuotationTransferToSalesOrder,
            },
            quantityColumn,
            {
                id: "discount",
                label: window.bo.cartTranslations.discount,
                type: "text",
                orderable: false,
                searchable: false,
                editable: false,
                transformer: (record) =>
                    cartItemDiscountSteps(record, window.bo.currencySymbol).join(" ") || "-",
            },
            {
                id: "unit_price_after_discount",
                label: window.bo.cartTranslations.unit_price_after_discount,
                type: "number",
                // Line items always display at 2dp, matching the DB precision - not
                // window.bo.siteCurrencyDecimalPlaces, which is 0 for TWD and is reserved for the
                // cart/document-level total.
                decimal: 2,
                orderable: false,
                searchable: false,
                editable: false,
                transformer: (record) => {
                    if (record._type == "bundle_cart_item") {
                        return record.getDisplayUnitPrice();
                    } else {
                        return record.getPrice();
                    }
                },
            },
            {
                id: "total",
                label: window.bo.cartTranslations.total_price,
                type: "number",
                decimal: 2,
                orderable: false,
                searchable: false,
                editable: false,
                transformer: (record) => {
                    if (record._type == "bundle_cart_item") {
                        return record.getDisplayUnitPrice() * record.quantity;
                    } else if (record._type == "child_bundle_cart_item") {
                        return record.getPrice() * record.quantity * record.composite_parent_quantity;
                    } else {
                        return record.getPrice() * record.quantity;
                    }
                },
            },
            {
                id: "weight",
                label: window.bo.generalTranslations.weight_with_unit,
                type: "text",
                orderable: false,
                searchable: false,
                editable: false,
                transformer: (record) => {
                    if (record.getWeight()) {
                        if (record._type == "child_bundle_cart_item") {
                            return (
                                this.toFixedNumber(
                                    this.toFixedNumber(record.getWeight()) *
                                        record.quantity *
                                        record.composite_parent_quantity
                                ) +
                                " " +
                                window.bo.generalTranslations.kg
                            );
                        }
                        return (
                            this.toFixedNumber(this.toFixedNumber(record.getWeight()) * record.quantity) +
                            " " +
                            window.bo.generalTranslations.kg
                        );
                    }
                },
            },
            {
                id: "dimensional_sum",
                label: window.bo.generalTranslations.dimensional_sum_with_unit,
                type: "text",
                orderable: false,
                searchable: false,
                editable: false,
                transformer: (record) => {
                    if (record.getDimensionalSum()) {
                        if (record._type == "child_bundle_cart_item") {
                            return (
                                this.toFixedNumber(
                                    this.toFixedNumber(record.getDimensionalSum()) *
                                        record.quantity *
                                        record.composite_parent_quantity
                                ) +
                                " " +
                                window.bo.generalTranslations.m
                            );
                        }
                        return (
                            this.toFixedNumber(this.toFixedNumber(record.getDimensionalSum()) * record.quantity) +
                            " " +
                            window.bo.generalTranslations.m
                        );
                    }
                },
            },
            {
                id: "volume",
                label: window.bo.generalTranslations.volume_with_unit,
                type: "text",
                orderable: false,
                searchable: false,
                editable: false,
                transformer: (record) => {
                    if (record.getVolume()) {
                        if (record._type == "child_bundle_cart_item") {
                            return (
                                this.toFixedNumber(
                                    this.toFixedNumber(record.getVolume()) *
                                        record.quantity *
                                        record.composite_parent_quantity
                                ) +
                                " " +
                                window.bo.generalTranslations.m3
                            );
                        }
                        return (
                            this.toFixedNumber(this.toFixedNumber(record.getVolume()) * record.quantity) +
                            " " +
                            window.bo.generalTranslations.m3
                        );
                    }
                },
            },
            {
                id: "remark",
                label: window.bo.cartTranslations.remark,
                type: "text",
                orderable: false,
                searchable: false,
                editable: true,
            },
            {
                label: window.bo.cartTranslations.error_message,
                id: "error_message",
                orderable: false,
                visibility: true,
                type: "text",
                searchable: false,
                editable: false,
                transformer: (record) => {
                    return this.stockShortItems[record.id] ?? "";
                },
            },
        ];

        if (window.bo.hideLineItemTax) {
            columns = columns.filter((col) => col.id !== "tax_name" && col.id !== "tax_percentage");
        }

        // Drop the money columns outright when prices are hidden, so they stay out of the
        // column-preference picker too.
        columns = filterCatalogPriceColumns(columns);

        let cartItemColumns = this.boDatatableProcessColumns(columns);

        columns.forEach((column) => {
            column.editable = false;
            column.visibility = true;

            if (column.id == "shipping_address") {
                column.transformer = () => {
                    return null;
                };
            }
        });

        let subrecordColumns = this.boDatatableProcessColumns(columns);

        if (!showLineItemShippingAddress) {
            delete cartItemColumns.shipping_address;
            delete subrecordColumns.shipping_address;
        }

        return {
            confirm_your_address: window.bo.cartTranslations.confirm_your_address,
            confirm_message: window.bo.cartTranslations.confirm_message,
            address_name: window.bo.cartTranslations.address_name,
            address: window.bo.cartTranslations.address,
            billing_address: window.bo.cartTranslations.billing_address,
            too_short: window.bo.cartTranslations.too_short,
            confirmPopup: false,
            shipping_address_value: {},
            cart: this.parseCart(this.filterCartItem(window.bo.cart)),
            cartLoaded: false,
            cartLoadError: false,
            cartUrl: window.bo.cartUrl,
            deprecatedCompanyFieldEnabled: window.bo.deprecatedCompanyFieldEnabled,
            showPhoneInAddress: window.bo.showPhoneInAddress,
            currentUserWarehouseInventoriesUrl: window.bo.currentUserWarehouseInventoriesUrl,
            APICartUrl: window.bo.APICartUrl,
            catalogUrl: window.bo.catalogUrl,
            paymentUrl: window.bo.paymentUrl,
            couponsUrl: window.bo.couponsUrl,
            manageAddressUrl: window.bo.manageAddressUrl,
            profileUrl: window.bo.profileUrl,
            outletSelectorUrl: window.bo.outletSelectorUrl,
            duitnowUrl: window.bo.duitnowUrl,
            razerUrl: window.bo.razerUrl,
            userLoggedIn: window.bo.loggedIn,
            outletSelected: window.bo.outletSelected,
            cartItemColumns: cartItemColumns,
            productLabelOptions: productLabelOptions,
            selectedProductLabels: [],
            subrecordColumns: subrecordColumns,
            deliveryAddress: null,
            deliveryAddresses: constructSearchListItems(
                deliveryAddresses.map((address) => {
                    address.hasQuery = searchProperties.bind(address);
                    return {
                        items: [address],
                    };
                })
            ),
            shippingOptions: {
                west: {
                    type: "flat_rate",
                    title: window.bo.cartTranslations.flat_rate,
                    total: 12,
                },
                east: {
                    type: "flat_rate",
                    title: window.bo.cartTranslations.flat_rate,
                    total: 15,
                },
                free: {
                    type: "free_shipping",
                    title: window.bo.cartTranslations.free_shipping,
                    total: 0,
                },
            },
            shippingMode: null,
            warehouse: this.parseWarehouse(window.bo.warehouse),
            shippingDate: window.bo?.cart?.preferred_delivery_date
                ? moment(window.bo.cart.preferred_delivery_date).toDate()
                : null,
            checkingOut: false,
            couponSettingEnabled: window.bo.couponSettingEnabled,
            selectCouponActive: false,
            coupons: null,
            selected_coupons: [],
            selected_coupon_code: "",
            saved: [],
            searchCoupon: "",
            pointConfig: window.bo.pointConfig,
            warehouseInventories: {},
            shippingMethodUrl: window.bo.shippingMethodUrl,
            boCloudSettings: window.bo.boCloudSettings,
            pageSettings: window.bo.pageSettings,
            shippingAddressCustomFields: shippingAddressCustomFields,
            paymentMode: null,
            paymentSettings: window.bo.paymentSettings,
            weightUnit: window.bo.weightUnit,
            isTaxExempted: window.bo.taxExempted,
            advancedUomStockQuantity: window.bo.advancedUomStockQuantity,
            roughStocks: window.bo.roughStocks,
            personalizedPayments: window.bo.personalizedPayments,
            customerTiers: window.bo.customerTiers || [],
            paymentSettingsList: window.bo.paymentSettingsList || [],

            generalTranslations: window.bo.generalTranslations,
            cartTranslations: window.bo.cartTranslations,
            headerTranslations: window.bo.headerTranslations,
            currencySymbol: window.bo.currencySymbol,
            siteCurrency: window.bo.siteCurrency,
            addressFormatMapper: window.bo?.addressFormatMapper,
            siteCurrencyDecimalPlaces: window.bo.siteCurrencyDecimalPlaces,
            lineItemUnitPriceMaxDecimalPlace: window.bo.lineItemUnitPriceMaxDecimalPlace,
            bundleCartItemError: {},

            // checkout addons
            checkoutAddons: window.bo.checkoutAddons,
            cartCheckoutAddons: {},
            isCartCheckoutAddonsValid: {},
            errors: {},
            quotationDocumentId: window.bo.quotationDocumentId,

            showNewAddressPopup: false,
            existingAddressIndex: 0,
            existingAddress: {},
            minDays: window.bo.minDays,

            // shipping
            shippingFeeTaxClass: window.bo.shippingFeeTaxClass,

            // round line item discount settings
            roundLineItemDiscount: window.bo.roundLineItemDiscount,

            // cart error messages
            cartErrorMessages: [],
            shippingErrorSyncPending: false,
            shippingErrorSyncReady: false,
            // Set once checkout is blocked by the shipping method, so the control is highlighted
            // only after the customer has actually tried to check out.
            shippingMethodValidationAttempted: false,

            maxLineItemQuantity: window.bo.maxLineItemQuantity,
            cartViewMode: window.boResolveCartViewMode(), // Same selection as the loading shell and view toggle.
        };
    },
    watch: {
        cartViewMode(newVal) {
            try {
                localStorage.setItem("b2b-cart-view-mode", newVal);
            } catch (error) {
                // The current selection still works when browser storage is unavailable.
            }
        },
        tabs(newValue) {
            if (newValue.length) {
                this.shippingMode = newValue[0].id;
            } else {
                this.shippingMode = null;
            }
        },
        cart: {
            handler() {
                if (this.cartLoaded) this.updateShippingDateIfNeeded();
            },
            deep: true,
            immediate: true,
        },
        shippingDate(newVal) {
            if (newVal && this.cartLoaded) {
                this.savePreferredDeliveryDate(newVal);
            }
        },
        selectedShippingMethod() {
            if (this.shippingErrorSyncPending) {
                this.syncShippingMethodErrors();
            }
        },
        paymentMode(newVal) {
            if (newVal) {
                this.clearCartErrors(
                    "empty_payment_mode_validation_error",
                    "invalid_payment_mode_error",
                    "missing_payment_mode_error"
                );
            }
        },
        cartErrorMessages: {
            handler(newVal) {
                // new changes detected, update error bar automatically instead of manually trigger
                window.dispatchEvent(
                    new CustomEvent("cart-error-updated", {
                        detail: newVal,
                    })
                );

                if (newVal.length > 0) {
                    const mainContainer = document.querySelector(".main-container");
                    if (mainContainer) {
                        mainContainer.style.paddingTop = "4rem";
                    }
                } else {
                    const mainContainer = document.querySelector(".main-container");
                    if (mainContainer) {
                        mainContainer.style.paddingTop = "";
                    }
                }
            },
            deep: true,
        },
    },
    computed: {
        /**
         * Red-highlights the shipping-method control once checkout has been blocked by it. Derived
         * rather than latched, so the highlight clears itself as soon as the method is changed or
         * the cart is adjusted to satisfy the rule.
         */
        shippingMethodInvalid() {
            return this.shippingMethodValidationAttempted && this.getShippingMethodAvailabilityMessage() !== "";
        },
        shippingMethodSubtotal() {
            return this.toFixedNumber(this.cartSubtotalWithTax, window.bo.siteCurrencyDecimalPlaces);
        },
        tabs() {
            const tabs = [];

            if (this.pageSettings.shipping_modes.delivery) {
                tabs.push({
                    id: "delivery",
                    title: window.bo.cartTranslations.delivery,
                });
            }

            if (this.pageSettings.shipping_modes.pickup) {
                tabs.push({
                    id: "pickup",
                    title: window.bo.cartTranslations.pickup,
                });
            }

            return tabs;
        },
        // rows of the table view: the cart items that carry every label picked in the Product Labels filter
        tableCartItems() {
            return filterItemsByLabels(this.cart.items, this.selectedProductLabels, this.cartLabelContext());
        },
        items: function items() {
            return Object.values(this.cart.items).sort((a, b) => {
                let aSku = null;
                if (a.product instanceof VariableProduct) {
                    aSku = a.product.variations[a.product_id].sku;
                } else if (a.product instanceof BundleProduct) {
                    aSku = a.product.sku;
                }
                let bSku = null;
                if (b.product instanceof VariableProduct) {
                    bSku = b.product.variations[b.product_id].sku;
                } else if (b.product instanceof BundleProduct) {
                    bSku = b.product.sku;
                }
                if (aSku < bSku) {
                    return -1;
                }
                if (bSku < aSku) {
                    return 1;
                }
                return 0;
            });
        },
        cartSubtotalWithTax: function cartSubtotalWithTax() {
            return this.cart.getSubtotalWithTaxForShippingCalculation();
        },
        cartSubtotal: function cartSubtotal() {
            return this.cart.getSubtotal();
        },
        cartDiscount: function cartDiscount() {
            return this.cart.getCartDiscount();
        },
        cartTotal: function cartTotal() {
            this.cart.shipping_fee = this.shippingTotal;
            return this.cart.getTotal();
        },
        cartTotalWithTax: function cartTotalWithTax() {
            this.cart.shipping_fee = this.shippingTotal;
            return this.cart.getTotalWithTax();
        },
        cartSaved: function cartSaved() {
            return this.totalSaved();
        },
        totalWeight: function totalWeight() {
            return this.toFixedNumber(
                Object.values(this.cart.items).reduce(
                    (acc, item) => acc + this.toFixedNumber(item.getWeight()) * item.quantity,
                    0
                )
            );
        },
        totalVolume: function totalVolume() {
            return this.toFixedNumber(
                Object.values(this.cart.items).reduce(
                    (acc, item) => acc + this.toFixedNumber(item.getVolume()) * item.quantity,
                    0
                )
            );
        },
        totalDimensionalSum: function totalDimensionalSum() {
            return this.toFixedNumber(
                Object.values(this.cart.items).reduce(
                    (acc, item) => acc + this.toFixedNumber(item.getDimensionalSum()) * item.quantity,
                    0
                )
            );
        },
        totalNumberOfItems: function totalNumberOfItems() {
            return Object.values(this.cart.items).reduce((acc, item) => acc + item.quantity, 0);
        },
        pointEarn: function pointEarn() {
            return parseFloat(
                (
                    parseFloat(this.pointConfig.tier_multiplier) *
                    this.cartSubtotal *
                    this.pointConfig.conversion_rate
                ).toFixed(2)
            );
        },
        shippingTotal() {
            if (this.userLoggedIn && this.hasDeliveryAddress) {
                if (this.shippingMode === "pickup") {
                    return 0;
                }
                if (this.shippingMode === "delivery") {
                    let shippingFee = this.shippingFees[this.shippingMethod?.id]?.shipping_fee ?? 0;
                    shippingFee = this.addOrRemoveTaxIfNeeded(this.shippingFeeTaxClass, shippingFee);

                    return shippingFee;
                }
            }

            return 0;
        },
        filteredCoupons: {
            get() {
                let coupons = {};
                let regExp = new RegExp(this.searchCoupon, "i");
                if (this.coupons != null) {
                    Object.values(this.coupons).forEach((coupon) => {
                        if (coupon.name.match(regExp) != null || coupon.code.match(regExp) != null) {
                            coupons[coupon.id] = coupon;
                            this.selected_coupons.forEach((selected_coupon) => {
                                if (coupon.id == selected_coupon.id) {
                                    delete coupons[coupon.id];
                                }
                            });
                        }
                    });
                }
                return coupons;
            },
        },
        taxes: {
            get() {
                return this.cart.getTaxes();
            },
        },
        showMinimalistStock() {
            return !(
                window.bo.hasCapabilityPhysical ||
                window.bo.hasCapabilitySellable ||
                window.bo.hasCapabilityRoughSellable ||
                !window.bo.hasWarehouse
            );
        },
    },
    mounted() {
        this.loadCartPageData();
    },
    methods: {
        ...utils,

        loadCartPageData() {
            this.cartLoadError = false;
            window.boShowCartNavigationLoading?.();
            this.getData(
                window.bo.cartPageDataUrl,
                null,
                (response) => {
                    const pageData = response.data;
                    if (!pageData?.cart || pageData.cart.items == null) {
                        this.cartLoadError = true;
                        window.boShowCartNavigationError?.(() => this.loadCartPageData());
                        return;
                    }
                    window.bo.invalidItems = pageData.invalidItems;
                    this.cart = this.parseCart(this.filterCartItem(pageData.cart));
                    this.minDays = pageData.minDays;
                    this.quotationDocumentId = pageData.quotationDocumentId;
                    this.shippingDate = pageData.cart.preferred_delivery_date
                        ? moment(pageData.cart.preferred_delivery_date).toDate()
                        : null;
                    this.cartLoaded = true;
                    this.$nextTick(() => {
                        try {
                            this.initializeLoadedCart();
                        } finally {
                            window.boFinishCartNavigation?.();
                        }
                    });
                },
                () => {
                    this.cartLoadError = true;
                    window.boShowCartNavigationError?.(() => this.loadCartPageData());
                },
                false
            );
        },

        initializeLoadedCart() {
            this.deliveryAddress = this.deliveryAddresses[0]?.items[0];
            // a saved cash on pickup payment opens on the pick-up tab, so it is not dropped
            const pickupTab = this.cart.payment_mode === "cash_on_pickup" && this.tabs.find((tab) => tab.id === "pickup");
            this.shippingMode = pickupTab ? pickupTab.id : this.tabs[0]?.id ?? null;
            this.preprocessItems();
            this.boProcessRecords(this.cart.items, this.items);

            const productIds = this.items.map((item) => item.product.id);

            this.setShippingInfo();
            this.updateShippingDateIfNeeded(false);
            this.refreshShippingFees(this.APICartUrl, this.cart.id, null, false);

            this.setShippingMethod();
            this.refreshShippingMethodAvailability(this.shippingDate, false);

            this.setPaymentMode();

            // From here the cart reflects real data, so shipping errors can be kept in sync with it.
            this.shippingErrorSyncReady = true;

            // Gather the inventories using the product_id and Assign to this.userWarehouseInventories
            if (this.currentUserWarehouseInventoriesUrl) {
                this.getCurrentUserWarehouseInventories(
                    {
                        params: { product_id: productIds.join(",") },
                    },
                    (warehouseInventories) => {
                        if (warehouseInventories && !this.advancedUomStockQuantity) {
                            warehouseInventories.forEach((baseWarehouseInventory) => {
                                Object.keys(this.items).forEach((itemKey) => {
                                    if (
                                        baseWarehouseInventory.product_parent_id == this.items[itemKey].product.id &&
                                        baseWarehouseInventory.conv_rate_group ==
                                            this.items[itemKey].product.variations[this.items[itemKey].product_id]
                                                .conv_rate_group
                                    ) {
                                        let item = this.items[itemKey];

                                        Object.keys(item.product.variations).forEach((variationId) => {
                                            const variation = item.product.variations[variationId];
                                            if (variation.id == baseWarehouseInventory.product_id) {
                                                return;
                                            }

                                            if (
                                                baseWarehouseInventory.product.conv_rate_group !=
                                                item.product.variations[item.product_id].conv_rate_group
                                            ) {
                                                return;
                                            }

                                            let stockBalance = this.getStockBalance(baseWarehouseInventory, variation);

                                            warehouseInventories.push({
                                                product_id: variation.id,
                                                stock_balance: stockBalance,
                                                balance: this.roundToDecimal(
                                                    baseWarehouseInventory.balance / variation.uom.conversion_rate
                                                ),
                                                physical_balance: this.roundToDecimal(
                                                    baseWarehouseInventory.physical_balance / variation.uom.conversion_rate
                                                ),
                                            });
                                        });
                                    } else if (this.items[itemKey].product instanceof BundleProduct) {
                                        let item = this.items[itemKey];

                                        Object.values(item.product.components).forEach((component) => {
                                            Object.values(component.products).forEach((product) => {
                                                Object.values(product.variations).forEach((variation) => {
                                                    if (variation.parent_id == baseWarehouseInventory.product_parent_id) {
                                                        let stockBalance = this.getStockBalance(
                                                            baseWarehouseInventory,
                                                            variation
                                                        );

                                                        warehouseInventories.push({
                                                            product_id: variation.id,
                                                            stock_balance: stockBalance,
                                                            balance: this.roundToDecimal(
                                                                baseWarehouseInventory.balance /
                                                                    variation.uom.conversion_rate
                                                            ),
                                                            physical_balance: this.roundToDecimal(
                                                                baseWarehouseInventory.physical_balance /
                                                                    variation.uom.conversion_rate
                                                            ),
                                                        });
                                                    }
                                                });
                                            });
                                        });
                                    }
                                });
                            });
                        }
                        this.boProcessRecords(this.warehouseInventories, warehouseInventories, "product_id");
                    },
                    null,
                    false
                );
            }
            this.updateBundleItemPrice();
            this.updateBundleItemCompositeParentQuantity();
            let errorProducts = [];
            let errorProductsCartItemId = [];
            Object.values(window.bo.invalidItems).forEach((invalidItem) => {
                if (!invalidItem.composite_parent_id && invalidItem.product?.name) {
                    const isBundle = invalidItem.product.type === "bundle";
                    const bundleLabel = window.bo.cartTranslations?.bundle || "Bundle";
                    const productName = isBundle
                        ? `${invalidItem.product.name} (${bundleLabel})`
                        : invalidItem.product.name;
                    errorProducts.push(productName);
                }
                errorProductsCartItemId.push(invalidItem.id);
            });
            errorProducts = errorProducts.filter((value, index, array) => array.indexOf(value) === index);
            if (errorProducts.length > 0) {
                const prompt = new Prompt(
                    () => {
                        // delete the invalid line item
                        this.deleteItems(errorProductsCartItemId);
                    },
                    () => {
                        // delete the invalid line item
                        this.deleteItems(errorProductsCartItemId);
                    },
                    window.bo.cartTranslations.product_not_available,
                    window.bo.cartTranslations.list_not_available.replace(":list", errorProducts.join(", ")),
                    window.bo.generalTranslations.ok,
                    "danger"
                );
                this.boPromptIfNeeded(prompt, true);
            }

            let cartCheckoutAddons = {};
            if (window.bo.enableQuotationTransferToSalesOrder && !window.bo.allowMultipleQuotationTransfer) {
                if (this.quotationDocumentId) {
                    this.getDocument(
                        this.quotationDocumentId,
                        null,
                        (document) => {
                            Object.values(this.checkoutAddons).forEach((checkoutAddon) => {
                                if (checkoutAddon.name == "Submit To IRBM") {
                                    cartCheckoutAddons[checkoutAddon.name] = document.custom_fields[checkoutAddon.name]
                                        ? document.custom_fields[checkoutAddon.name] == "yes"
                                        : true;
                                } else if (document.custom_fields[checkoutAddon.name]) {
                                    cartCheckoutAddons[checkoutAddon.name] = document.custom_fields[checkoutAddon.name];
                                }
                                if (checkoutAddon.type == "file") {
                                    let mediaObject = {};

                                    if (document.custom_fields[checkoutAddon.name]) {
                                        for (const media of document.custom_fields[checkoutAddon.name]) {
                                            mediaObject[media.id] = media;
                                        }
                                    }
                                    cartCheckoutAddons[checkoutAddon.name] = mediaObject;
                                }
                            });
                            this.cartCheckoutAddons = cartCheckoutAddons;
                        },
                        (error) => {
                            console.error("Error fetching document:");
                            console.log(error);
                        }
                    );
                }
            } else {
                Object.values(this.checkoutAddons).forEach((checkoutAddon) => {
                    if (checkoutAddon.name == "Submit To IRBM") {
                        cartCheckoutAddons[checkoutAddon.name] = this.cart.custom_fields[
                            "start_buying_" + checkoutAddon.name
                        ]
                            ? this.cart.custom_fields["start_buying_" + checkoutAddon.name] == "yes"
                            : true;
                    } else if (this.cart.custom_fields["start_buying_" + checkoutAddon.name]) {
                        cartCheckoutAddons[checkoutAddon.name] =
                            this.cart.custom_fields["start_buying_" + checkoutAddon.name];
                    }
                    if (checkoutAddon.type == "file") {
                        let mediaObject = {};

                        if (this.cart.custom_fields["start_buying_" + checkoutAddon.name]) {
                            for (const media of this.cart.custom_fields["start_buying_" + checkoutAddon.name]) {
                                mediaObject[media.id] = media;
                            }
                        }
                        cartCheckoutAddons[checkoutAddon.name] = mediaObject;
                    }
                });
                this.cartCheckoutAddons = cartCheckoutAddons;

                Object.values(this.checkoutAddons).forEach((checkoutAddon) => {
                    this.$set(this.isCartCheckoutAddonsValid, checkoutAddon.name, true);
                    if (
                        checkoutAddon.type == "select" &&
                        checkoutAddon.options.length > 0 &&
                        this.cartCheckoutAddons[checkoutAddon.name] === undefined
                    ) {
                        this.$set(this.cartCheckoutAddons, checkoutAddon.name, checkoutAddon.options[0].value);
                    }
                });
            }
        },

        getLineDiscountSteps(item) {
            return cartItemDiscountSteps(item, this.currencySymbol);
        },

        shippingFeeDecimalPlaces(fee) {
            return this.getShippingFeeDecimalPlaces(fee, this.siteCurrencyDecimalPlaces);
        },

        openLoginPopup() {
            $("#loginButton").trigger("click");
        },

        setCartViewMode(mode) {
            this.cartViewMode = mode;
        },

        toggleCartViewMode() {
            this.cartViewMode = this.cartViewMode === "table" ? "list" : "table";
        },

        // what decides which labels apply: the same backorder rule as the table's Backorder column
        cartLabelContext() {
            return {
                canReadPrices: this.canReadPrices,
                hasInventory: !!window.bo.hasInventory,
                allowBackorder: (item) => item.product.type != "bundle" && !!this.getProductAllowBackorder(item.product),
            };
        },

        cartItemLabels(item) {
            const textById = Object.fromEntries(this.productLabelOptions.map((label) => [label.id, label.text]));

            return getCartItemLabelIds(item, this.cartLabelContext()).map((id) => ({ id, text: textById[id] }));
        },

        updateBundleItemPrice() {
            // update price in bundle
            Object.values(this.cart.items).forEach((item) => {
                if (item._type == "bundle_cart_item") {
                    Object.values(item.composite_children).forEach((composite_child) => {
                        composite_child.updatePrice();
                    });

                    Object.values(item.subrecords).forEach((subrecord) => {
                        subrecord.updatePrice();
                    });
                }
            });
        },
        updateBundleItemCompositeParentQuantity() {
            // update composite_parent_quantity in bundle
            // NOTE: updateBundleItemPrice() runs getTotal() over BOTH composite_children and subrecords,
            // so the parent quantity MUST be set on both (they can hold different object references).
            Object.values(this.cart.items).forEach((item) => {
                if (item._type == "bundle_cart_item") {
                    const parentQuantity = item.quantity;
                    Object.values(item.composite_children).forEach((composite_child) => {
                        composite_child.composite_parent_quantity = parentQuantity;
                    });
                    Object.values(item.subrecords).forEach((subrecord) => {
                        subrecord.composite_parent_quantity = parentQuantity;
                    });
                }
            });
        },
        initializeShippingDate(settings, warehouse) {
            // NOTE: The server mirrors this delivery-date min-date calculation in
            // app/Services/Agent/CartMutations/CartShippingService.php
            // (resolveDeliveryDateConstraint). If the logic here changes, update
            // that backend method to match.
            let date = new Date();
            const ordering = window.bo?.boCloudSettings?.ordering ?? {};
            const logisticsLeadTime = parseInt(window.bo?.boCloudSettings?.logistics?.logistics?.lead_time) || 0;
            const deliveryTime = settings?.delivery_time ?? false;

            let minDays = logisticsLeadTime;

            const isPickup = this.shippingMode === "pickup";
            const delayHours = isPickup
                ? parseInt(ordering.pickup_delay_hours) || 0
                : parseInt(ordering.delivery_delay_hours) || 0;

            if (deliveryTime && delayHours > 0) {
                date.setHours(date.getHours() + delayHours);
            } else {
                const modeDelayDays = isPickup
                    ? parseInt(ordering.pickup_delay_days) || 0
                    : parseInt(ordering.delivery_delay_days) || 0;
                if (modeDelayDays > minDays) {
                    minDays = modeDelayDays;
                }
            }

            const cartObj = this.cart || window.bo?.cart;
            if (cartObj && cartObj.items) {
                const items = Array.isArray(cartObj.items) ? cartObj.items : Object.values(cartObj.items);
                items.forEach((item) => {
                    const product = item?.product || item;
                    const productLeadTime = parseInt(product?.lead_time) || 0;
                    if (productLeadTime > minDays) {
                        minDays = productLeadTime;
                    }
                });
            }

            if (minDays > 0) {
                if (window.bo.hasWarehouse && warehouse) {
                    let workingDaysAdded = 0;
                    while (workingDaysAdded < minDays) {
                        date.setDate(date.getDate() + 1);

                        if (!warehouse.isOffDay(date)) {
                            workingDaysAdded++;
                        }
                    }
                } else {
                    date.setDate(date.getDate() + minDays);
                }
            }

            let dayCounter = 0;
            if (window.bo.hasWarehouse && warehouse) {
                while (warehouse.isOffDay(date) && dayCounter < 7) {
                    date.setDate(date.getDate() + 1);
                    dayCounter++;
                }
                if (dayCounter >= 7) {
                    date = new Date();
                }
            }

            if (!deliveryTime) {
                date.setHours(0, 0, 0, 0);
            }

            return date;
        },
        updateShippingDateIfNeeded(showLoadingScreen = true) {
            if (!window.bo || !window.bo.pageSettings) return;
            const newMinDate = this.initializeShippingDate(
                window.bo.pageSettings,
                this.parseWarehouse(window.bo.warehouse)
            );

            let candidateDate =
                this.cart?.preferred_delivery_date || window.bo?.cart?.preferred_delivery_date || this.shippingDate;
            if (candidateDate && typeof candidateDate === "string") {
                candidateDate = moment(candidateDate).toDate();
            }
            let isValid = false;

            if (candidateDate) {
                const candidateTime = new Date(candidateDate).getTime();
                const minTime = new Date(newMinDate).getTime();
                const isOffDay = this.getWarehouseAvailableDeliveryDate
                    ? this.getWarehouseAvailableDeliveryDate(new Date(candidateDate))
                    : false;

                if (!isNaN(candidateTime) && candidateTime >= minTime && !isOffDay) {
                    isValid = true;
                }
            }

            if (isValid) {
                const candidateTime = new Date(candidateDate).getTime();
                const currentTime = this.shippingDate ? new Date(this.shippingDate).getTime() : null;
                if (currentTime !== candidateTime) {
                    this.shippingDate = candidateDate;
                    this.refreshShippingMethodAvailability(this.shippingDate, showLoadingScreen);
                }
            } else {
                const minTime = new Date(newMinDate).getTime();
                const currentTime = this.shippingDate ? new Date(this.shippingDate).getTime() : null;
                if (currentTime !== minTime) {
                    this.shippingDate = newMinDate;
                    this.refreshShippingMethodAvailability(this.shippingDate, showLoadingScreen);
                }
                this.savePreferredDeliveryDate(newMinDate);
            }
        },
        changeItemQuantity(id) {
            let item = this.cart.items[id];

            if (item._type == "bundle_cart_item") {
                this.updateBundleItemCompositeParentQuantity();
                this.updateBundleItemPrice();
                const payload = {
                    id: item.id,
                    quantity: item.quantity,
                    total: item.getTotal(),
                    total_tax: item.getTotalTax(),
                    subtotal: item.getSubtotal(),
                    subtotal_tax: item.getSubtotalTax(),
                };

                this.updateCartItems(this.cartUrl, [payload], (response) => {
                    if (response && response.data && response.data.cart) {
                        this.cart = this.parseCart(this.filterCartItem(response.data.cart));
                        this.boProcessRecords(this.cart.items, this.items);

                        // the backend returns each line already priced with its own volume-aware
                        // trade agreement price, so just recompute bundle totals from it
                        this.updateBundleItemCompositeParentQuantity();
                        this.updateBundleItemPrice();
                    }
                    this.setCoupons();
                    this.refreshShippingFees(this.APICartUrl, this.cart.id);
                    this.syncShippingMethodErrors();
                });
                return;
            }

            // wait for the trade agreement price to resolve for the new quantity before
            // computing total/subtotal, otherwise the persisted cart item (and eventually
            // the checkout total) would be built from the stale, pre-change price
            this.reloadTradeAgreementPrice(item, item.quantity, () => {
                const payload = {
                    id: item.id,
                    quantity: item.quantity,
                    total: item.getTotal(),
                    subtotal: item.getSubtotal(),
                };

                this.updateCartItems(this.cartUrl, [payload], (response) => {
                    if (response && response.data && response.data.cart) {
                        this.cart = this.parseCart(this.filterCartItem(response.data.cart));
                        this.boProcessRecords(this.cart.items, this.items);

                        // the backend returns each line already priced with its own volume-aware
                        // trade agreement price, so just recompute bundle totals from it
                        this.updateBundleItemCompositeParentQuantity();
                        this.updateBundleItemPrice();
                    }
                    this.setCoupons();
                    this.refreshShippingFees(this.APICartUrl, this.cart.id);
                    this.syncShippingMethodErrors();
                    this.refreshShippingMethodAvailability(this.shippingDate);
                });
            });
        },
        // for updating the line item shipping address
        changeLineItemShippingAddress(id) {
            let item = this.cart.items[id];
            this.updateCartItems(this.cartUrl, [
                {
                    id: item.id,
                    shipping_address: item.shipping_address,
                },
            ]);
        },
        updateItemRemark(id, remark) {
            let item = this.cart.items[id];
            item.remark = remark;

            this.updateCartItems(
                this.cartUrl,
                [
                    {
                        id: item.id,
                        remark: item.remark,
                    },
                ],
                (response) => {
                    if (response && response.data && response.data.cart) {
                        this.cart = this.parseCart(this.filterCartItem(response.data.cart));
                        this.boProcessRecords(this.cart.items, this.items);
                    }
                }
            );
        },
        savePreferredDeliveryDate: _.debounce(function (shippingDate) {
            const cartId = this.cart?.id || window.bo?.cart?.id;
            if (!cartId || !shippingDate) return;

            const formattedDate = moment(shippingDate).toISOString();
            const url = `${this.APICartUrl}/${cartId}`;
            this.putData(url, { preferred_delivery_date: formattedDate }, null, null, false);
        }, 400),
        onShippingDateChange(val) {
            this.refreshShippingMethodAvailability(val);
            this.savePreferredDeliveryDate(val);
        },
        changeSelectedUom(id, uom) {
            const item = this.cart.items[id];
            item.selectUom(uom);

            // see changeItemQuantity for why the trade agreement price must be resolved
            // before the payload (total/subtotal) is built and sent
            this.reloadTradeAgreementPrice(item, item.quantity, () => {
                const payload = {
                    id: item.id,
                    product_id: item.product_id,
                    quantity: item.quantity,
                    total: item.getTotal(),
                    subtotal: item.getSubtotal(),
                };

                this.updateCartItems(this.cartUrl, [payload], (response) => {
                    if (response && response.data && response.data.cart) {
                        this.cart = this.parseCart(this.filterCartItem(response.data.cart));
                        this.boProcessRecords(this.cart.items, this.items);

                        // the backend returns each line already priced with its own volume-aware
                        // trade agreement price (see changeItemQuantity), so just recompute bundle totals
                        this.updateBundleItemCompositeParentQuantity();
                        this.updateBundleItemPrice();
                    }
                    this.setCoupons();
                    this.refreshShippingFees(this.APICartUrl, this.cart.id);
                    this.syncShippingMethodErrors();
                    this.refreshShippingMethodAvailability(this.shippingDate);
                });
            });
        },
        setShippingInfo() {
            if (this.shippingMode == "delivery") {
                if (!this.isEmptyAddress(this.cart.shipping_address)) {
                    this.deliveryAddress = this.cart.shipping_address;
                }
            } else {
                this.deliveryAddress = null;
            }
        },
        setShippingMethod() {
            if (this.shippingMode == "delivery") {
                if (this.shippingZone && this.cart.shipping_method) {
                    this.setSelectedShippingMethod(this.shippingZone.shipping_methods[this.cart.shipping_method], true);
                }
            }
        },
        isEmptyAddress(address) {
            return (
                !address ||
                Object.values(address).every((v) => v === null || (typeof v === "string" && v.trim() === ""))
            );
        },
        changeShippingMode(mode) {
            this.shippingMode = mode;

            // Delivery-only errors don't apply to pick-up; re-check the rest against the new mode.
            if (mode == "pickup") {
                this.clearCartErrors("cart_delivery_address_validation_error");
            }
            this.syncShippingMethodErrors();

            if (
                ((mode == "delivery" && !this.pageSettings.online_banking.enabled_for_delivery) ||
                    (mode == "pickup" && !this.pageSettings.online_banking.enabled_for_pickup)) &&
                this.paymentMode === "online_banking"
            ) {
                this.paymentMode = null;
            }

            // reset payment mode for cash on delivery when change shipping mode
            if (
                mode == "pickup" &&
                this.boCloudSettings?.["settings-payment"]?.enable_cash_on_delivery_payment_method &&
                this.paymentMode === "cash_on_delivery"
            ) {
                this.paymentMode = null;

                this.cartErrorMessages?.forEach((error, key) => {
                    if (error.id == "cash_on_delivery_exceeded_max_amount") {
                        this.cartErrorMessages.splice(key, 1);
                    }
                });
            }

            // reset payment mode for cash on pickup when change shipping mode
            if (
                mode == "delivery" &&
                this.boCloudSettings?.["settings-payment"]?.enable_cash_on_pickup_payment_method &&
                this.paymentMode === "cash_on_pickup"
            ) {
                this.paymentMode = null;
            }
        },
        changeShippingMethod(method) {
            this.setSelectedShippingMethod(this.shippingZone.shipping_methods[method.id], true);

            this.clearCartErrors(
                "shipping_method_validation_error",
                "empty_shipping_method_validation_error",
                "cart_checkout_missing_shipping_method"
            );

            if (this.shippingMode == "delivery") {
                this.validateDocumentShippingMethodAvailability();
            }
        },
        clearCartErrors(...ids) {
            if (!this.cartErrorMessages || this.cartErrorMessages.length === 0) {
                return;
            }

            this.cartErrorMessages = this.cartErrorMessages.filter((error) => !ids.includes(error.id));
        },
        /**
         * Drops shipping errors left over from an earlier cart / address state, then re-checks the
         * selected method against the current cart so the banner reflects the cart as it is now.
         */
        syncShippingMethodErrors() {
            // Skip while the cart is still loading (e.g. the shipping tab firing its initial change), otherwise
            // the half-initialised cart flashes a shipping error that disappears once loading finishes.
            if (!this.shippingErrorSyncReady) {
                return;
            }

            this.clearCartErrors("shipping_method_availability", "shipping_method_validation_error");

            // After an address change the selected method is re-pointed (by name) at the new zone's
            // method a tick later; until then it isn't in the zone, so re-sync once it has been swapped.
            const inZone = !!this.shippingZone?.shipping_methods?.[this.selectedShippingMethod?.id];
            this.shippingErrorSyncPending = this.shippingMode == "delivery" && !inZone;
            const message = this.shippingMode == "delivery" && inZone ? this.getShippingMethodAvailabilityMessage() : "";
            if (message) {
                this.cartErrorMessages = this.cartErrorMessages ?? [];
                this.pushError("shipping_method_availability", message);
            }
        },
        changeDeliveryAddress(address) {
            this.deliveryAddress = address;
            this.refreshShippingFees(this.APICartUrl, this.cart.id);
            this.syncShippingMethodErrors();
            this.refreshShippingMethodAvailability(this.shippingDate);
        },
        setPaymentMode() {
            let cartPaymentMode = this.cart.payment_mode ?? null;
            if (this.isEmpty(cartPaymentMode)) {
                this.paymentMode = null;
                return;
            }

            let paymentEnabled = false;
            const enabledPersonalisedPayment = this.paymentSettings.enable_personalised_payment;
            const allowedPersonalisedPayment = (type) => {
                return enabledPersonalisedPayment ? this.personalizedPayments[type] : true;
            };

            if (cartPaymentMode == "online_banking") {
                paymentEnabled =
                    this.paymentSettings.enable_submerchant_fpx_payment_method &&
                    ((this.shippingMode == "delivery" && this.pageSettings.online_banking.enabled_for_delivery) ||
                        (this.shippingMode == "pickup" && this.pageSettings.online_banking.enabled_for_pickup)) &&
                    allowedPersonalisedPayment("cash");
            } else if (cartPaymentMode == "credit_terms") {
                paymentEnabled =
                    this.paymentSettings.enable_credit_terms_payment_method && allowedPersonalisedPayment("credit");
            } else if (cartPaymentMode == "cash") {
                paymentEnabled =
                    this.boCloudSettings?.["settings-payment"]?.enable_cash_payment_method &&
                    allowedPersonalisedPayment("cash");
            } else if (cartPaymentMode == "cash_on_delivery") {
                paymentEnabled =
                    this.shippingMode == "delivery" &&
                    this.boCloudSettings?.["settings-payment"]?.enable_cash_on_delivery_payment_method &&
                    allowedPersonalisedPayment("cash");
            } else if (cartPaymentMode == "cash_on_pickup") {
                paymentEnabled =
                    this.shippingMode == "pickup" &&
                    this.boCloudSettings?.["settings-payment"]?.enable_cash_on_pickup_payment_method &&
                    allowedPersonalisedPayment("cash");
            } else if (cartPaymentMode == "duitnow") {
                paymentEnabled =
                    this.paymentSettings.enable_duitnow_payment_method && allowedPersonalisedPayment("cash");
            } else if (cartPaymentMode == "prepaid_credits") {
                paymentEnabled =
                    this.paymentSettings.enable_prepaid_credits_payment_method && allowedPersonalisedPayment("prepaid");
            }

            this.paymentMode = paymentEnabled ? cartPaymentMode : null;
        },
        validateCart() {
            if (this.cart.getQuantity() < 1) {
                this.pushError(
                    "cart_item_quantity_validation_error",
                    window.bo.cartTranslations.cart_item_quantity_validation_error
                );
            }

            const requiresPayment = this.paymentSettings.enable_payment && this.cartTotalWithTax > 0;
            if (requiresPayment && this.paymentMode == null) {
                this.pushError(
                    "empty_payment_mode_validation_error",
                    window.bo.cartTranslations.empty_payment_mode_validation_error
                );
            }

            let productCounts = {};
            for (const item of this.items) {
                productCounts[item.product_id] = {
                    count: (productCounts[item.product_id]?.count ?? 0) + item.quantity,
                    item: item,
                };
            }
            Object.keys(productCounts).forEach((id) => {
                const item = productCounts[id].item;
                const product = item.product;

                if (
                    window.bo.enableQuotationTransferToSalesOrder &&
                    productCounts[id].count > item.line_item.untransferred_quantity
                ) {
                    this.pushError(
                        "line_item_quantity_validation_error",
                        window.bo.cartTranslations.line_item_quantity_validation_error
                            .replace(":sku", product.sku)
                            .replace(":quantity", item.line_item.untransferred_quantity)
                            .replace(":count", productCounts[id].count)
                    );
                } else {
                    let earliestDeliveryDate = moment().add(product.lead_time, "d").toDate();

                    if (!this.pageSettings.delivery_time) {
                        earliestDeliveryDate = new Date(earliestDeliveryDate.setHours(0, 0, 0, 0));
                    }

                    if (
                        productCounts[id].count > (this.warehouseInventories[id]?.balance ?? 0) &&
                        product.lead_time != null &&
                        dateDayDifference(this.shippingDate, earliestDeliveryDate) < 0
                    ) {
                        this.pushError(
                            `delivery_lead_time_validation_error_${product.sku}`,
                            window.bo.cartTranslations.delivery_lead_time_validation_error
                                .replace(":sku", product.sku)
                                .replace(":date", moment(earliestDeliveryDate).format("DD/MM/YYYY"))
                        );
                    }
                }
            });

            this.validateCartStock();

            if (this.shippingMode == null) {
                if (window.bo.enableEditShippingAddress) {
                    this.pushError(
                        "empty_shipping_method_validation_error",
                        window.bo.cartTranslations.empty_shipping_method_validation_error.replace(
                            ":url",
                            this.manageAddressUrl
                        )
                    );
                } else {
                    this.pushError(
                        "empty_shipping_method_validation_error",
                        window.bo.cartTranslations.empty_shipping_method_validation_error_contact_sales_rep
                    );
                }
            }

            if (!this.hasDeliveryAddress && this.shippingMode === "delivery") {
                if (window.bo.enableEditShippingAddress) {
                    this.pushError(
                        "cart_delivery_address_validation_error",
                        window.bo.cartTranslations.cart_delivery_address_validation_error.replace(
                            ":url",
                            this.manageAddressUrl
                        )
                    );
                } else {
                    this.pushError(
                        "cart_delivery_address_validation_error",
                        window.bo.cartTranslations.cart_delivery_address_validation_error_contact_sales_rep
                    );
                }
            }

            if (this.shippingZone && !this.shippingMethod && this.shippingMode === "delivery") {
                this.pushError(
                    "shipping_method_validation_error",
                    window.bo.cartTranslations.shipping_method_validation_error
                );
            }

            const couponCount = this.selected_coupons.length;
            for (const selected_coupon of this.selected_coupons) {
                // validate individual use
                if (selected_coupon.individual_use && couponCount > 1) {
                    this.pushError(
                        `coupon_individual_use_validation_error_${selected_coupon.code}`,
                        window.bo.cartTranslations.coupon_individual_use_validation_error.replace(
                            ":code",
                            selected_coupon.code
                        )
                    );
                }

                // validate purchase quantity
                const maximumProductQuantity =
                    selected_coupon.variation_quantity_remaining ??
                    selected_coupon.variation_quantity_limit ??
                    selected_coupon.remaining_product_quantity ??
                    selected_coupon.maximum_product_quantity;
                const minimumProductQuantity = selected_coupon.minimum_product_quantity;
                if (maximumProductQuantity != null || minimumProductQuantity != null) {
                    let exceedMaximumProductQuantity = false;
                    let exceedMinimumProductQuantity = false;
                    let count = selected_coupon.currentPurchaseQuantity;

                    if (maximumProductQuantity != null && count > maximumProductQuantity) {
                        exceedMaximumProductQuantity = true;
                    }

                    if (minimumProductQuantity != null && count < minimumProductQuantity) {
                        exceedMinimumProductQuantity = true;
                    }

                    if (
                        maximumProductQuantity != null &&
                        minimumProductQuantity != null &&
                        (exceedMaximumProductQuantity || exceedMinimumProductQuantity)
                    ) {
                        this.pushError(
                            `coupon_minimum_maximum_product_quantity_validation_error_${selected_coupon.code}`,
                            window.bo.cartTranslations.coupon_minimum_maximum_product_quantity_validation_error
                                .replace(":code", selected_coupon.code)
                                .replace(":minimum_quantity", minimumProductQuantity)
                                .replace(":maximum_quantity", maximumProductQuantity)
                        );
                    } else if (exceedMaximumProductQuantity) {
                        this.pushError(
                            `coupon_maximum_product_quantity_validation_error_${selected_coupon.code}`,
                            window.bo.cartTranslations.coupon_maximum_product_quantity_validation_error
                                .replace(":code", selected_coupon.code)
                                .replace(":maximum_quantity", maximumProductQuantity)
                        );
                    } else if (exceedMinimumProductQuantity) {
                        this.pushError(
                            `coupon_minimum_product_quantity_validation_error_${selected_coupon.code}`,
                            window.bo.cartTranslations.coupon_minimum_product_quantity_validation_error
                                .replace(":code", selected_coupon.code)
                                .replace(":minimum_quantity", minimumProductQuantity)
                        );
                    }
                }

                // validate minimum document amount
                if (selected_coupon.min_document_amount != null) {
                    if (selected_coupon.currentTotal < selected_coupon.min_document_amount) {
                        this.pushError(
                            `coupon_minimum_price_is_not_met_validation_error_${selected_coupon.code}`,
                            window.bo.cartTranslations.coupon_minimum_price_is_not_met_validation_error.replace(
                                ":code",
                                selected_coupon.code
                            )
                        );
                    }
                }
            }

            if (this.shippingMode == "delivery") {
                // this.validateDocumentShippingMethodShippingClass();
                // this.validateDocumentShippingMethodWeight();
                this.validateDocumentShippingMethodAvailability();
            }

            return this.cartErrorMessages.length === 0 && this.validateRequiredField(true);
        },
        checkoutUserCart() {
            if (!this.userLoggedIn) {
                /* HACK
                 * What is happening here is that the login component exists on a different Vue app, using a separate Vue instance.
                 * Thus, any form of communication between the two Vue instances will require a global Vue object (and thus changes in ALL vue components) or an external event bus.
                 *
                 * This is a temporary work around. The login button has also been assigned an id for future usage.
                 */
                this.openLoginPopup();
                return;
            }

            this.cartErrorMessages = [];

            if (!this.validateCart()) {
                // this.displayMessages();
                return;
            }

            this.checkingOut = true;
            this.checkoutCart(
                this.cartUrl,
                {
                    mode: "start_buying",
                    cart: {
                        id: this.cart.id,
                        items: Object.values(this.cart.items).map((item) => {
                            let subtotal = 0;
                            let total = 0;
                            if (item._type == "bundle_cart_item") {
                                subtotal = item.getSubtotal();
                                total = item.getTotal();
                            } else {
                                subtotal = item.getSubtotalWithoutTax();
                                total = item.getTotalWithoutTax();
                            }
                            let subtotalTax = item.isTaxExempted ? 0 : item.getSubtotalTax();
                            let totalTax = item.isTaxExempted ? 0 : item.getTotalTax();
                            let unitPrice = item.getOriginalPrice();

                            if (this.siteCurrency === "TWD") {
                                // Line items are always submitted at 2dp (not 0dp), matching how
                                // they're stored/rounded server-side - the cart/document-level
                                // total then rounds once per tax class (matching Lingyue).
                                subtotal = Math.round(subtotal * 100) / 100;
                                total = Math.round(total * 100) / 100;
                                subtotalTax = 0;
                                totalTax = 0;
                            }

                            return {
                                id: item.id,
                                product_id: item.product_id,
                                quantity: item.quantity,
                                subtotal: subtotal,
                                total: total,
                                subtotal_tax: subtotalTax,
                                total_tax: totalTax,
                                unit_price: unitPrice,
                                remark: item.remark,
                                shipping_address: item.shipping_address,
                                child_items: item.composite_children
                                    ? Object.values(item.composite_children).map((child_item) => {
                                          let childItemSubtotal = child_item.getSubtotalWithoutTax();
                                          let childItemTotal = child_item.getTotalWithoutTax();
                                          let childItemSubtotalTax = item.isTaxExempted ? 0 : child_item.subtotal_tax;
                                          let childItemTotalTax = item.isTaxExempted ? 0 : child_item.total_tax;
                                          let childItemUnitPrice = child_item.getOriginalPrice();
                                          return {
                                              id: child_item.id,
                                              product_id: child_item.product_id,
                                              quantity: child_item.quantity * item.quantity,
                                              subtotal: childItemSubtotal,
                                              subtotal_tax: childItemSubtotalTax,
                                              total: childItemTotal,
                                              total_tax: childItemTotalTax,
                                              unit_price: childItemUnitPrice,
                                              composite_parent_id: child_item.composite_parent_id,
                                              component_id: child_item.component_id,
                                              remark: child_item.remark,
                                          };
                                      })
                                    : null,
                            };
                        }),
                        remark: this.cart.remark,
                        attachments: Object.values(this.cart.attachments).map((attachment) => attachment.id),
                        checkout_add_ons: this.getCheckoutAddons(),
                        shipping_address: this.deliveryAddress,
                        shipping_method: this.shippingMethod?.id,
                        payment_mode: this.paymentMode,
                    },
                    coupon_lines: this.cart.coupons.map((coupon) => {
                        return {
                            id: coupon.id,
                            code: coupon.code,
                            amount:
                                coupon.discount_type == "unit_amount"
                                    ? this.calculateAbsoluteDiscount(coupon)
                                    : coupon.discount_amount,
                        };
                    }),
                    shipping_mode: this.shippingMode,
                    shipping_date: this.shippingDate,
                    cart_total_with_tax: this.cartTotalWithTax,
                },
                (documentId) => {
                    window.location.assign(`${this.cartUrl}/${documentId}`);
                },
                (error) => {
                    this.checkingOut = false;

                    if (450 == error.response.status) {
                        // custom exception
                        this.pushError("cart_error", error.response.data.errors.message);
                    } else if (error.response.data.code == "outlet_not_selected") {
                        this.boPromptStaticIfNeeded(
                            new Prompt(() => {
                                window.location.assign(`${this.outletSelectorUrl}?next=${this.cartUrl}`);
                            }),
                            error.response.data.code == "outlet_not_selected",
                            "select_outlet_dialog"
                        );
                    } else if (error.response.data.code == "prevent_adding_to_cart_error_rough_sellable") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.prevent_adding_to_cart_error_rough_sellable
                        );
                    } else if (error.response.data.code == "billing_address_incomplete") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.billing_address_incomplete.replace(":url", this.profileUrl)
                        );
                    } else if (error.response.data.code == "shipping_info_incomplete_address") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.enableEditShippingAddress
                                ? window.bo.cartTranslations.shipping_info_incomplete_address.replace(
                                      ":url",
                                      this.manageAddressUrl
                                  )
                                : window.bo.cartTranslations.shipping_info_incomplete_address_contact_sales_rep
                        );
                    } else if (error.response.data.code == "shipping_info_incomplete_address_custom_fields") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.enableEditShippingAddress
                                ? window.bo.cartTranslations.shipping_info_incomplete_address_custom_fields.replace(
                                      ":url",
                                      this.manageAddressUrl
                                  )
                                : window.bo.cartTranslations
                                      .shipping_info_incomplete_address_custom_fields_contact_sales_rep
                        );
                    } else if (error.response.data.code == "shipping_info_missing_address") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.enableEditShippingAddress
                                ? window.bo.cartTranslations.shipping_info_missing_address.replace(
                                      ":url",
                                      this.manageAddressUrl
                                  )
                                : window.bo.cartTranslations.shipping_info_missing_address_contact_sales_rep
                        );
                    } else if (error.response.data.code == "cart_checkout_insufficient_order_time") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.cart_checkout_insufficient_order_time
                                .replace(":amount", error.response.data.data.interval)
                                .replace(":mode", this.shippingMode)
                        );
                    } else if (error.response.data.code == "cart_checkout_product_backorder_forbidden") {
                        const data = error.response.data.data;
                        this.pushError(
                            error.response.data.code,
                            this.getStockShortMessage(data.product_sku, data.requested_quantity, data.sellable_quantity, data.uom)
                        );
                    } else if (error.response.data.code == "cart_checkout_insufficient_min_order_amount") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.cart_checkout_insufficient_min_order_amount
                                .replace(":amount", error.response.data.data.min_order_amount)
                                .replace(":name", this.shippingMethod.name)
                        );
                    } else if (error.response.data.code == "transfer_document_expired_error") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.transfer_document_expired_error.replace(
                                ":list",
                                error.response.data.data.id.join("</p><p>")
                            )
                        );
                    } else if (error.response.data.code == "invalid_line_item_error") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.invalid_line_item_error.replace(
                                ":list",
                                error.response.data.data.id.join("</p><p>")
                            )
                        );
                    } else if (error.response.data.code == "multiple_quotation_transfer_not_allowed_error") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.multiple_quotation_transfer_not_allowed_error
                        );
                    } else if (error.response.data.code == "cart_checkout_empty_item_quantity") {
                        this.pushError(
                            error.response.data.code,
                            window.bo.cartTranslations.cart_checkout_empty_item_quantity.replace(
                                ":list",
                                error.response.data.data.map((item) => item.product_sku).join("</p><p>")
                            )
                        );
                    } else if (error.response.data.code == "cart_checkout_invalid_item") {
                        this.boPromptIfNeeded(
                            new Prompt(
                                () => {
                                    window.location.reload();
                                },
                                () => {
                                    window.location.reload();
                                },
                                window.bo.cartTranslations.cart_checkout_invalid_item
                            ),
                            true
                        );
                    } else {
                        this.pushError(error.response.data.code, error.response.data.message);
                    }
                }
            );
        },
        pushError(code, msg) {
            this.cartErrorMessages.push({
                id: code,
                error_message: msg,
                updated_at: new Date().toISOString(),
            });
        },
        deleteItems(items) {
            this.deleteCartItems(this.cartUrl, items, (response) => {
                items.forEach((id) => {
                    this.$delete(this.cart.items, id);
                });

                this.setCoupons();
                this.refreshShippingFees(this.APICartUrl, this.cart.id);
                this.syncShippingMethodErrors();
                this.shippingDate = this.initializeShippingDate(
                    window.bo.pageSettings,
                    this.parseWarehouse(window.bo.warehouse)
                );
            });
        },
        loadConsumerCoupons(payload, success, failure) {
            const current = new Date();

            this.getConsumerCoupons(payload, (couponLogs) => {
                this.coupons = {};
                Object.values(couponLogs).forEach((couponLog) => {
                    if (
                        couponLog.document_id == null &&
                        new Date(couponLog.coupon.start_at) <= current &&
                        new Date(couponLog.coupon.end_at) > current
                    ) {
                        this.$set(this.coupons, couponLog.coupon.id, couponLog.coupon);
                        this.coupons[couponLog.coupon.id].active = true;
                        this.coupons[couponLog.coupon.id].action = window.bo.cartTranslations.use;
                    }
                });
                if (success) success(this.coupons);
            });
        },
        fetchConsumerCoupons(payload = null, success = null, failure = null) {
            this.selected_coupon_code = "";
            if (this.coupons == null) {
                if (payload == null) {
                    payload = {
                        params: {
                            includeNoRedemptionCoupons: true,
                        },
                    };
                } else {
                    payload.params = payload.params ?? {};
                    payload.params.includeNoRedemptionCoupons = true;
                }
                this.loadConsumerCoupons(payload, success, failure);
            }
        },
        applyCoupon(selectedCoupon) {
            const selectedCouponId = selectedCoupon?.id;
            if (selectedCouponId == null) return;

            const coupon = this.coupons?.[selectedCouponId];
            if (!coupon) return;

            if (this.selected_coupons.some((existingCoupon) => existingCoupon.id == coupon.id)) {
                this.selectCouponActive = false;
                this.setCoupons();
                return;
            }

            if (coupon.discount_type == "unit_amount") {
                this.selected_coupons.unshift(coupon);
            } else {
                this.selected_coupons.push(coupon);
            }

            this.selectCouponActive = false;
            this.setCoupons();
        },
        // calculateDiscount(coupon) {
        //     let tempSubtotal = this.cartSubtotal;
        //     let couponIndex = this.selected_coupons.findIndex(
        //         (selected_coupon) => {
        //             return selected_coupon.id == coupon.id;
        //         }
        //     );
        //     for (
        //         let i = this.selected_coupons.length - 1;
        //         i >= couponIndex;
        //         i--
        //     ) {
        //         if (i == couponIndex) {
        //             tempSubtotal =
        //                 (tempSubtotal /
        //                     (100 -
        //                         parseFloat(
        //                             this.selected_coupons[i].discount
        //                         ))) *
        //                 parseFloat(this.selected_coupons[i].discount);
        //             this.saved[couponIndex] = tempSubtotal;
        //             return tempSubtotal;
        //         } else {
        //             tempSubtotal =
        //                 (tempSubtotal /
        //                     (100 -
        //                         parseFloat(
        //                             this.selected_coupons[i].discount
        //                         ))) *
        //                 100;
        //         }
        //     }
        //     return 0;
        // },
        unapplyCoupon(unapplyCoupon) {
            this.selected_coupons = this.selected_coupons.filter((coupon) => coupon.id != unapplyCoupon.id);
            this.setCoupons();
        },
        applyCoupons(documentId, success = null, failure = null) {
            let couponIds = [];
            this.selected_coupons.forEach((selected_coupon) => couponIds.push(selected_coupon.id));
            this.postApplyCoupon({
                document_id: documentId,
                coupon_id: couponIds,
            });
        },
        setCoupons() {
            const selectedCouponIds = this.selected_coupons.map((coupon) => coupon.id);
            // Normalise selected coupons by ID so recalculation always uses canonical coupon objects
            // This fixes issues where coupons are applied, then cart is updated causing the coupon objects in selected_coupons to be out of sync with the coupon objects in coupons which would cause incorrect recalculation
            const normalisedSelectedCoupons = selectedCouponIds
                .map((id) => this.coupons?.[id] ?? this.selected_coupons.find((coupon) => coupon.id == id))
                .filter((coupon) => coupon != null);

            this.selected_coupons = this.cart.setCoupons(normalisedSelectedCoupons);
            this.cart.items = { ...this.cart.items };
        },
        totalSaved() {
            let totalSaved = this.cart.total_absolute_discount + this.cart.getCartDiscount();
            this.selected_coupons.forEach((couponSaved) => {
                if (couponSaved.discount_type != "unit_amount" && !this.cart.isCartDiscountLineCoupon(couponSaved)) {
                    totalSaved += parseFloat(couponSaved.discount_amount);
                }
            });
            return totalSaved;
        },
        calculateAbsoluteDiscount(coupon) {
            // Booked as its own cart discount line, so it is not part of total_absolute_discount.
            if (this.cart.isCartDiscountLineCoupon(coupon)) {
                return coupon.discount_amount;
            }
            let totalAbsoluteDiscount = this.cart.total_absolute_discount;
            let amountToReturn = 0;
            for (const selected_coupon of this.selected_coupons) {
                if (this.cart.isCartDiscountLineCoupon(selected_coupon)) continue;
                const selectedDiscount = selected_coupon.applied_discount_amount ?? selected_coupon.discount;
                if (selected_coupon.id == coupon.id) {
                    amountToReturn =
                        selectedDiscount > totalAbsoluteDiscount ? totalAbsoluteDiscount : selectedDiscount;
                    break;
                }
                totalAbsoluteDiscount -= selectedDiscount;
            }
            return parseFloat(amountToReturn);
        },
        disableDateCallback(date) {
            return this.warehouse.isOffDay(date);
        },
        disableTimeCallback(date) {
            return !this.warehouse.isWorkingHours(date);
        },
        filterCartItems(payload, success, failure) {},
        changeAttachments(attachments) {
            this.updateAttachments(
                this.APICartUrl,
                this.cart.id,
                Object.values(attachments).map((attachment) => attachment.id),
                (attachments) => {
                    this.cart.attachments = keyBy(attachments, "id");
                }
            );
        },
        getShippingAddressCustomFieldValue(field) {
            if (field.value == null || field.value == "") {
                return window.bo.cartTranslations.none;
            }
            return field.value;
        },
        getShippingAddressCustomFieldSelectOption(key, field) {
            return (
                this.shippingAddressCustomFields[key]?.options[field.value ?? null]?.label ??
                window.bo.cartTranslations.none
            );
        },
        preprocessItems() {
            Object.values(this.cart.items).forEach((item) => {
                // item.discount = item.getPrice() == item.getOriginalPrice() ? 0 : (1 - item.getPrice() )
            });
        },
        reloadTradeAgreementPrice(item, quantity, onComplete) {
            this.getProductTradeAgreementPrice(
                this.cartUrl,
                item.product instanceof VariableProduct ? item.product.selected_variation_id : item.product.id,
                {
                    quantity: quantity ?? item.quantity,
                },
                (productPrice) => {
                    if (productPrice) {
                        // the trade-agreement endpoint returns the discount as `type` / `formula`
                        // (discount_type / discount_formula are the cart item's field names)
                        if (item.product instanceof VariableProduct) {
                            item.product.variations[item.product.selected_variation_id].regular_price =
                                productPrice.regular_price;
                            item.product.variations[item.product.selected_variation_id].sale_price =
                                productPrice.sale_price;
                            item.product.variations[item.product.selected_variation_id].discount_type =
                                productPrice.type ?? null;
                            item.product.variations[item.product.selected_variation_id].discount_formula =
                                productPrice.formula ?? null;
                        } else {
                            item.product.regular_price = productPrice.regular_price;
                            item.product.sale_price = productPrice.sale_price;
                            item.product.discount_type = productPrice.type ?? null;
                            item.product.discount_formula = productPrice.formula ?? null;
                        }
                    }

                    if (onComplete) onComplete();
                },
                () => {
                    if (onComplete) onComplete();
                }
            );
        },
        filterCartItem(cart) {
            let cartItems = [];
            Object.values(cart.items).forEach((item) => {
                if (item.mode == "start_buying") {
                    cartItems.push(item);
                }
            });
            cart.items = cartItems;
            return cart;
        },
        getWarehouseAvailableDeliveryDate(date) {
            let disable = false;
            if (this.pageSettings.delivery_time) {
                disable = disable || date < new Date();
            } else {
                disable = disable || date < new Date(new Date().setHours(0, 0, 0, 0));
            }
            if (window.bo.hasWarehouse) {
                disable = disable || this.warehouse.isOffDay(date);
            }
            return disable;
        },
        getCalculatedMinDays() {
            // NOTE: The server mirrors this delivery-date min-day calculation in
            // app/Services/Agent/CartMutations/CartShippingService.php
            // (resolveDeliveryDateConstraint). If the logic here changes, update
            // that backend method to match.
            const ordering = window.bo?.boCloudSettings?.ordering ?? {};
            const logisticsLeadTime = parseInt(window.bo?.boCloudSettings?.logistics?.logistics?.lead_time) || 0;
            const deliveryTime = this.pageSettings?.delivery_time ?? false;

            const isPickup = this.shippingMode === "pickup";
            const delayHours = isPickup
                ? parseInt(ordering.pickup_delay_hours) || 0
                : parseInt(ordering.delivery_delay_hours) || 0;

            let modeDelayDays = 0;
            if (!deliveryTime || delayHours <= 0) {
                modeDelayDays = isPickup
                    ? parseInt(ordering.pickup_delay_days) || 0
                    : parseInt(ordering.delivery_delay_days) || 0;
            }

            let maxLeadTime = Math.max(logisticsLeadTime, modeDelayDays);

            const cartObj = this.cart || window.bo?.cart;
            if (cartObj && cartObj.items) {
                const items = Array.isArray(cartObj.items) ? cartObj.items : Object.values(cartObj.items);
                items.forEach((item) => {
                    const product = item?.product || item;
                    const productLeadTime = parseInt(product?.lead_time) || 0;
                    if (productLeadTime > maxLeadTime) {
                        maxLeadTime = productLeadTime;
                    }
                });
            }

            return maxLeadTime;
        },
        roundToDecimal(number, decimal = 2) {
            return parseFloat(Number(number).toFixed(decimal));
        },
        getMinShippingDate() {
            return this.initializeShippingDate(
                this.pageSettings || window.bo?.pageSettings,
                this.parseWarehouse(window.bo?.warehouse)
            );
        },
        getMinDays() {
            return this.getMinShippingDate();
        },
        getStockBalance(baseWarehouseInventory, variation) {
            if (window.bo.hasCapabilityPhysicalStocks) {
                let sellable = this.roundToDecimal(baseWarehouseInventory.balance / variation.uom.conversion_rate);
                let physical = this.roundToDecimal(
                    baseWarehouseInventory.physical_balance / variation.uom.conversion_rate
                );
                return sellable + "s / " + physical + "p";
            }
            if (window.bo.hasCapabilitySellableStocks) {
                return window.bo.cartTranslations.amount_in_stock.replace(
                    ":amount",
                    this.roundToDecimal(baseWarehouseInventory.balance / variation.uom.conversion_rate)
                );
            }
            if (window.bo.hasCapabilityRoughSellableStocks) {
                let roughStockMessage = "";

                for (let i = 0; i < this.roughStocks.length; i++) {
                    let balance = this.roundToDecimal(baseWarehouseInventory.balance / variation.uom.conversion_rate);
                    if (this.roughStocks[i]["comparison"] == ">") {
                        if (balance > this.roughStocks[i]["quantity"]) {
                            roughStockMessage = this.roughStocks[i]["display_as"];
                            break;
                        }
                    } else if (this.roughStocks[i]["comparison"] == "<") {
                        if (balance < this.roughStocks[i]["quantity"]) {
                            roughStockMessage = this.roughStocks[i]["display_as"];
                            break;
                        } else {
                            roughStockMessage = window.bo.cartTranslations.amount_in_stock.replace(":amount", balance);
                            break;
                        }
                    }
                }

                return roughStockMessage;
            }

            if (baseWarehouseInventory.balance > 0) return window.bo.cartTranslations.in_stock;
            return window.bo.cartTranslations.out_of_stock;
        },
        addTaxIfNeeded(total, total_tax) {
            let result = 0;

            if (
                (window.bo.taxIncluded && !window.bo.displayPricesDuringCartAndCheckoutIncludingTax) ||
                window.bo.taxIncluded == window.bo.displayPricesDuringCartAndCheckoutIncludingTax
            ) {
                result = total;
            } else {
                result = total + total_tax;
            }

            return result.toFixed(2);
        },
        getBundleCartItemError(cartItemId, componentId, productId) {
            if (typeof this.bundleCartItemError[cartItemId] != "undefined") {
                if (typeof this.bundleCartItemError[cartItemId][componentId] != "undefined") {
                    if (typeof this.bundleCartItemError[cartItemId][componentId][productId] != "undefined") {
                        return this.bundleCartItemError[cartItemId][componentId][productId];
                    }
                }
            }

            return "";
        },
        showBundleComponentOutOfStockStatus(cartItemId, componentId, productId) {
            let inStockStatus = this.getBundleComponentInStockStatus(cartItemId, componentId, productId);
            let backorderStatus = this.getBundleComponentBackorderStatus(cartItemId, componentId, productId);

            if (inStockStatus == true || (this.showMinimalistStock === true && backorderStatus == true)) {
                return false;
            }

            return !inStockStatus;
        },
        getBundleComponentInStockStatus(cartItemId, componentId, productId) {
            if (typeof this.bundleCartItemError[cartItemId] != "undefined") {
                if (typeof this.bundleCartItemError[cartItemId][componentId] != "undefined") {
                    if (typeof this.bundleCartItemError[cartItemId][componentId][productId] != "undefined") {
                        return this.bundleCartItemError[cartItemId][componentId][productId]["in_stock_status"];
                    }
                }
            }
            return false;
        },
        showBundleComponentBackorderStatus(cartItemId, componentId, productId) {
            let inStockStatus = this.getBundleComponentInStockStatus(cartItemId, componentId, productId);
            let backorderStatus = this.getBundleComponentBackorderStatus(cartItemId, componentId, productId);

            if (backorderStatus == false || (this.showMinimalistStock === true && inStockStatus == true)) {
                return false;
            }

            return backorderStatus;
        },
        getBundleComponentBackorderStatus(cartItemId, componentId, productId) {
            if (typeof this.bundleCartItemError[cartItemId] != "undefined") {
                if (typeof this.bundleCartItemError[cartItemId][componentId] != "undefined") {
                    if (typeof this.bundleCartItemError[cartItemId][componentId][productId] != "undefined") {
                        return this.bundleCartItemError[cartItemId][componentId][productId]["allow_backorder"];
                    }
                }
            }
            return false;
        },
        editItemDetails(record) {
            window.location.assign(`${record.product_details_url}?cartItemId=${record.id}`);
        },
        getErrorMessage(field) {
            if (this.errors[field]) {
                return this.errors[field];
            }
            return null;
        },
        validateRequiredField(scroolNeeded = false) {
            let isNotValid = false;
            let element = null;
            // loop every checkout addons to check is the check out add on is required field / compulsory
            // for the non loop checkout addons will use other way to check the value
            // so when add new check out addon need to check if the field is always show one or not if yes need to add new if
            Object.values(this.checkoutAddons).forEach((checkoutAddon) => {
                if (checkoutAddon.compulsory || checkoutAddon.status_compulsory) {
                    if (checkoutAddon.name == "Shipping Date") {
                        this.$set(
                            this.isCartCheckoutAddonsValid,
                            checkoutAddon.name,
                            this.documentDeliveryDate != null
                        );
                        element =
                            this.isCartCheckoutAddonsValid[checkoutAddon.name] == true
                                ? element
                                : this.$refs[checkoutAddon.name];
                    } else if (checkoutAddon.name == "Remark") {
                        this.$set(
                            this.isCartCheckoutAddonsValid,
                            checkoutAddon.name,
                            this.cart.remark != null && this.cart.remark != ""
                        );
                        element =
                            this.isCartCheckoutAddonsValid[checkoutAddon.name] == true
                                ? element
                                : this.$refs[checkoutAddon.name];
                    } else if (checkoutAddon.name == "Shipping Provider") {
                        this.$set(
                            this.isCartCheckoutAddonsValid,
                            checkoutAddon.name,
                            this.documentShippingProvider != undefined
                        );
                        element =
                            this.isCartCheckoutAddonsValid[checkoutAddon.name] == true
                                ? element
                                : this.$refs[checkoutAddon.name];
                    } else if (checkoutAddon.name == "Attachments") {
                        this.$set(
                            this.isCartCheckoutAddonsValid,
                            checkoutAddon.name,
                            !(this.documentAttachment.length == 0 || Object.keys(this.documentAttachment).length == 0)
                        );
                        element =
                            this.isCartCheckoutAddonsValid[checkoutAddon.name] == true
                                ? element
                                : this.$refs[checkoutAddon.name];
                    } else {
                        let isValid = false;
                        if (
                            checkoutAddon.type == "text" ||
                            checkoutAddon.type == "textarea" ||
                            checkoutAddon.type == "select"
                        ) {
                            isValid =
                                this.cartCheckoutAddons[checkoutAddon.name] != null &&
                                this.cartCheckoutAddons[checkoutAddon.name].trim() != "";
                        } else if (checkoutAddon.type == "file") {
                            isValid = Object.keys(this.cartCheckoutAddons[checkoutAddon.name]).length > 0;
                        } else {
                            isValid = this.cartCheckoutAddons[checkoutAddon.name] != null;
                        }
                        this.$set(this.isCartCheckoutAddonsValid, checkoutAddon.name, isValid);
                        element =
                            this.isCartCheckoutAddonsValid[checkoutAddon.name] == true
                                ? element
                                : this.$refs[checkoutAddon.name][0];
                    }
                    isNotValid = isNotValid || !this.isCartCheckoutAddonsValid[checkoutAddon.name];
                }
            });
            if (element && scroolNeeded) {
                element.scrollIntoView({ behavior: "smooth" });
            }
            return !isNotValid;
        },
        changeCustomFieldAttachment(newCustomFieldCartAttachment, customFieldName) {
            this.$set(this.cartCheckoutAddons, customFieldName, newCustomFieldCartAttachment);
            this.validateRequiredField();
        },
        getCheckoutAddons() {
            let checkoutAddons = {};

            // checkoutAddons["Remark"] = {
            //     value: this.cart.remark,
            // };

            let cartCheckoutAddons = this.cartCheckoutAddons;

            Object.values(this.checkoutAddons).forEach((checkoutAddon) => {
                if (cartCheckoutAddons[checkoutAddon.name] != undefined && checkoutAddon.name == "Submit To IRBM") {
                    checkoutAddons[checkoutAddon.name] = {
                        mode: "start_buying",
                        value: cartCheckoutAddons[checkoutAddon.name] ? "yes" : "no",
                    };
                } else if (
                    typeof cartCheckoutAddons[checkoutAddon.name] != "undefined" &&
                    checkoutAddon.name != "Remark"
                ) {
                    checkoutAddons[checkoutAddon.name] = {
                        mode: "start_buying",
                        value: cartCheckoutAddons[checkoutAddon.name],
                    };
                    if (checkoutAddon.type == "file") {
                        checkoutAddons[checkoutAddon.name].values = [];
                        Object.values(cartCheckoutAddons[checkoutAddon.name]).forEach((media) => {
                            checkoutAddons[checkoutAddon.name].values.push(media.id);
                        });
                    }
                }
            });

            return checkoutAddons;
        },
        showCreateShippingAddressModal() {
            this.existingAddressIndex = 0;
            this.existingAddress = {};
            this.showNewAddressPopup = true;
        },
        activateConfirmPopup(value) {
            this.shipping_address_value = value;
            this.confirmPopup = true;
        },
        deactivateConfirmPopup() {
            this.confirmPopup = false;
        },
        upsertShippingAddress() {
            if (Object.keys(this.existingAddress).length === 0) {
                this.createShippingAddress(this.shipping_address_value);
            } else {
                this.updateShippingAddress(this.shipping_address_value);
            }
        },
        updateShippingAddress(value) {
            return this.postData(
                window.bo.createShippingAddressUrl + "/" + this.existingAddressIndex,
                value,
                (response) => {
                    this.deliveryAddresses = constructSearchListItems(
                        response.data.map((address, index) => {
                            address.hasQuery = searchProperties.bind(address);

                            if (address.custom_fields) {
                                address.custom_fields = keyBy(address.custom_fields, "slug");
                            }
                            address.index = index;

                            return {
                                items: [address],
                            };
                        })
                    );
                    this.deliveryAddress = this.deliveryAddresses[this.deliveryAddress.index ?? 0].items[0];
                },
                (error) => {}
            );
        },
        createShippingAddress(value) {
            return this.postData(
                window.bo.createShippingAddressUrl,
                value,
                (response) => {
                    this.deliveryAddresses = constructSearchListItems(
                        response.data.map((address, index) => {
                            address.hasQuery = searchProperties.bind(address);

                            if (address.custom_fields) {
                                address.custom_fields = keyBy(address.custom_fields, "slug");
                            }
                            address.index = index;

                            return {
                                items: [address],
                            };
                        })
                    );
                },
                (error) => {}
            );
        },
        showEditShippingAddressModal(shippingAddress) {
            this.existingAddressIndex = shippingAddress.index;
            this.existingAddress = shippingAddress;
            this.showNewAddressPopup = true;
        },

        // taxes
        addOrRemoveTaxIfNeeded(taxClass, price) {
            return this.cart.addOrRemoveTaxIfNeeded(taxClass, price);
        },

        getAllShippingClasses(shippingClasses, displayOnly = true) {
            let data = [];

            if (!this.isEmpty(shippingClasses) && Object.values(shippingClasses).length > 0) {
                if (!(Object.values(shippingClasses).length == 1 && Object.values(shippingClasses)?.[0]?.id == "all")) {
                    Object.values(shippingClasses).forEach((shippingClass) => {
                        data.push(shippingClass.name);
                    });
                }
            }

            return displayOnly ? data.join(", ") : data;
        },
        // validateDocumentShippingMethodShippingClass() {
        //     let pass = true;
        //     let currentShippingMethodClasses = this.selectedShippingMethod?.shipping_classes
        //         ? this.getAllShippingClasses(this.selectedShippingMethod?.shipping_classes, false)
        //         : [];

        //     if (currentShippingMethodClasses.length > 0) {
        //         if (
        //             !(
        //                 currentShippingMethodClasses.length == 1 &&
        //                 currentShippingMethodClasses[0] == this.generalTranslations.all
        //             )
        //         ) {
        //             if (Object.values(this.items).length > 0) {
        //                 Object.values(this.items).forEach((item) => {
        //                     if (item._type == "bundle_cart_item") {
        //                         Object.values(item.composite_children).forEach((children) => {
        //                             let childrenShippingClass =
        //                                 children["product"]["variations"][children["product_id"]]["shipping_class"];

        //                             let childrenShippingClassName = childrenShippingClass?.name ?? null;

        //                             if (
        //                                 this.isEmpty(childrenShippingClassName) ||
        //                                 !currentShippingMethodClasses.includes(childrenShippingClassName)
        //                             ) {
        //                                 pass = false;
        //                             }
        //                         });
        //                     } else {
        //                         let itemShippingClass =
        //                             item?.product?.getSelectedVariation()?.shipping_class?.name ?? "";

        //                         if (!currentShippingMethodClasses.includes(itemShippingClass)) {
        //                             pass = false;
        //                         }
        //                     }
        //                 });
        //             }
        //         }
        //     }

        //     if (!pass) {
        //         this.pushError(
        //             "invalid_shipping_method_by_shipping_class",
        //             this.cartTranslations.invalid_shipping_method_by_shipping_class
        //         );
        //     } else {
        //         this.cartErrorMessages.forEach((error, key) => {
        //             if (error.id == "invalid_shipping_method_by_shipping_class") {
        //                 this.cartErrorMessages.splice(key, 1);
        //             }
        //         });
        //     }
        // },
        getShippingMethodMinTableRateLabel(shippingMethod) {
            let translation = "";

            if (shippingMethod?.min_table_rate > 0) {
                if (shippingMethod.table_rate_unit == "kg") {
                    translation = this.generalTranslations.min_weight ?? "Min. Wt.: :amount :unit";
                } else if (shippingMethod.table_rate_unit == "m³") {
                    translation = this.generalTranslations.min_volume ?? "Min. Vol.: :amount :unit";
                } else if (shippingMethod.table_rate_unit == "m") {
                    translation = this.generalTranslations.min_dimensional_sum ?? "Min. DIM: :amount :unit";
                }

                translation =
                    translation != ""
                        ? translation
                              .replace(":amount", shippingMethod.min_table_rate)
                              .replace(":unit", shippingMethod.table_rate_unit)
                        : "";
            }

            return translation;
        },
        getShippingMethodMaxTableRateLabel(shippingMethod) {
            let translation = "";

            if (shippingMethod?.max_table_rate > 0) {
                if (shippingMethod.table_rate_unit == "kg") {
                    translation = this.generalTranslations.max_weight ?? "Max. Wt.: :amount :unit";
                } else if (shippingMethod.table_rate_unit == "m³") {
                    translation = this.generalTranslations.max_volume ?? "Max. Vol.: :amount :unit";
                } else if (shippingMethod.table_rate_unit == "m") {
                    translation = this.generalTranslations.max_dimensional_sum ?? "Max. DIM: :amount :unit";
                }

                translation =
                    translation != ""
                        ? translation
                              .replace(":amount", shippingMethod.max_table_rate)
                              .replace(":unit", shippingMethod.table_rate_unit)
                        : "";
            }

            return translation;
        },
        // validateDocumentShippingMethodWeight() {
        //     this.cartErrorMessages.forEach((error, key) => {
        //         if (
        //             error.id == "shipping_method_min_table_rate_not_fulfilled" ||
        //             error.id == "shipping_method_max_table_rate_not_fulfilled"
        //         ) {
        //             this.cartErrorMessages.splice(key, 1);
        //         }
        //     });

        //     if (this.selectedShippingMethod != null && this.selectedShippingMethod.type == "table_rate") {
        //         let tableRateUnit = this.selectedShippingMethod.table_rate_unit;
        //         let totalWeight = 0;

        //         if (Object.values(this.items).length > 0) {
        //             Object.values(this.items).forEach((item) => {
        //                 let currentTotal = 0;
        //                 let selectedVariation = null;

        //                 if (item._type == "bundle_cart_item") {
        //                     Object.values(item.composite_children).forEach((children) => {
        //                         currentTotal = 0;
        //                         selectedVariation = children["product"]["variations"][children["product_id"]];

        //                         if (tableRateUnit == "kg") {
        //                             currentTotal += selectedVariation?.weight ?? 0;
        //                         } else if (tableRateUnit == "m³") {
        //                             let length = selectedVariation?.length ?? 0;
        //                             let width = selectedVariation?.width ?? 0;
        //                             let height = selectedVariation?.height ?? 0;

        //                             currentTotal += length * width * height;
        //                         } else if (tableRateUnit == "m") {
        //                             let length = selectedVariation?.length ?? 0;
        //                             let width = selectedVariation?.width ?? 0;
        //                             let height = selectedVariation?.height ?? 0;

        //                             currentTotal += length + width + height;
        //                         }

        //                         if (currentTotal > 0) {
        //                             totalWeight += currentTotal * children["quantity"];
        //                         }
        //                     });
        //                 } else {
        //                     selectedVariation = item.product.getSelectedVariation();

        //                     if (tableRateUnit == "kg") {
        //                         currentTotal += selectedVariation?.weight ?? 0;
        //                     } else if (tableRateUnit == "m³") {
        //                         let length = selectedVariation?.length ?? 0;
        //                         let width = selectedVariation?.width ?? 0;
        //                         let height = selectedVariation?.height ?? 0;

        //                         currentTotal += length * width * height;
        //                     } else if (tableRateUnit == "m") {
        //                         let length = selectedVariation?.length ?? 0;
        //                         let width = selectedVariation?.width ?? 0;
        //                         let height = selectedVariation?.height ?? 0;

        //                         currentTotal += length + width + height;
        //                     }

        //                     if (currentTotal > 0) {
        //                         totalWeight += currentTotal * item.quantity;
        //                     }
        //                 }
        //             });
        //         }

        //         let errorObject = "";
        //         let errorMessage = "";

        //         if (tableRateUnit == "kg") {
        //             errorObject = this.generalTranslations.weight ?? "weight";
        //         } else if (tableRateUnit == "m³") {
        //             errorObject = this.generalTranslations.volume ?? "volume";
        //         } else if (tableRateUnit == "m") {
        //             errorObject = this.generalTranslations.dimensional_sum ?? "dimensional sum";
        //         }

        //         if (
        //             !this.isEmpty(this.selectedShippingMethod.max_table_rate) &&
        //             totalWeight > this.selectedShippingMethod.max_table_rate
        //         ) {
        //             errorMessage =
        //                 this.generalTranslations.shipping_method_max_table_rate_not_fulfilled ??
        //                 "Shipping method: The total :object (:total :unit) cannot be more than :max_amount :unit.";

        //             this.pushError(
        //                 "shipping_method_max_table_rate_not_fulfilled",
        //                 errorMessage
        //                     .replace(":object", errorObject)
        //                     .replace(":total", totalWeight)
        //                     .replace(":max_amount", this.selectedShippingMethod.max_table_rate)
        //                     .replaceAll(":unit", tableRateUnit)
        //             );
        //         } else if (totalWeight < this.selectedShippingMethod.min_table_rate) {
        //             errorMessage =
        //                 this.generalTranslations.shipping_method_min_table_rate_not_fulfilled ??
        //                 "Shipping method: The total :object (:total :unit) must be at least :min_amount :unit.";

        //             this.pushError(
        //                 "shipping_method_min_table_rate_not_fulfilled",
        //                 errorMessage
        //                     .replace(":object", errorObject)
        //                     .replace(":total", totalWeight)
        //                     .replace(":min_amount", this.selectedShippingMethod.min_table_rate)
        //                     .replaceAll(":unit", tableRateUnit)
        //             );
        //         }
        //     }
        // },
        /**
         * Why the selected shipping method can't be checked out with, or "" when it can. Side-effect
         * free, so the checkout validation and the invalid-field highlight can share it.
         */
        getShippingMethodAvailabilityMessage() {
            if (this.selectedShippingMethod == null) {
                return "";
            }

            // Covers shipping class, min/max spend, table rate and quota — composed by the
            // shipping-method-calculations mixin, which also feeds the message shown inside the
            // shipping-method control.
            return this.composeShippingMethodAvailabilityMessage(
                this.selectedShippingMethod,
                moment(this.shippingDate).format("DD/MM/YYYY")
            );
        },
        validateDocumentShippingMethodAvailability() {
            this.cartErrorMessages.forEach((error, key) => {
                if (error.id == "shipping_method_availability") {
                    this.cartErrorMessages.splice(key, 1);
                }
            });

            const message = this.getShippingMethodAvailabilityMessage();

            if (message) {
                this.pushError("shipping_method_availability", message);
                // Point the customer at the control, which shows the same reason in context.
                this.shippingMethodValidationAttempted = true;
                this.$nextTick(() => this.scrollToShippingMethod());
            }
        },
        scrollToShippingMethod() {
            const shippingMethodField = this.$refs.shippingMethodField;
            const element = Array.isArray(shippingMethodField) ? shippingMethodField[0] : shippingMethodField;

            element?.scrollIntoView({ behavior: "smooth", block: "center" });
        },
        getSingleLineAddress(address, options = {}) {
            return this.renderAddress(address, "single", options);
        },
        getMultiLineAddress(address, options = {}) {
            return this.renderAddress(address, "multiple", options);
        },
        renderAddress(address, rule, options = {}) {
            const config = this.addressFormatMapper;

            const fields = config.fields;
            const joins = config.join;
            const hideFields = options.hide_fields ?? [];

            const chunks = [];

            fields.forEach((field) => {
                if (hideFields.includes(field)) return;

                const value = address[field];
                if (!value) return;

                chunks.push({
                    key: field,
                    value: value.replace(/\s+/g, " ").replace(/,+$/, "").trim(),
                });
            });

            if (!chunks.length) return "";

            let out = chunks[0].value;

            for (let i = 1; i < chunks.length; i++) {
                const pair = `${chunks[i - 1].key}|${chunks[i].key}`;
                const sep = joins[rule][pair] ?? joins[rule].default;

                out += sep + chunks[i].value;
            }

            return out.replace(/\n+/g, "\n").trim();
        },
        isPaymentMethodAllowed(methodName) {
            const configTypeMap = {
                credit_terms: "credit_terms",
                prepaid_credits: "prepaid_credits",
                online_banking: "submerchant_fpx",
                online_payment: "newebpay",
                duitnow: "duitnowqr",
                cash_on_delivery: "cash_on_delivery",
                cash_on_pickup: "cash_on_pickup",
            };
            const configType = configTypeMap[methodName];
            if (!configType || !this.paymentSettingsList) return false;

            const methodConfig = this.paymentSettingsList.find((m) => m.type === configType);

            // Tier compatibility check (higher priority)
            let hasTiers = methodConfig && methodConfig.tiers && methodConfig.tiers.length > 0;
            if (hasTiers) {
                const customerTiers = window.bo.customerTiers || [];
                const matchesTier = methodConfig.tiers.some((tier) => customerTiers.includes(tier.name));
                if (matchesTier) return true; // Tier matches, bypass personalized check
            }

            // If method has tiers but customer doesn't match any, it is blocked
            if (hasTiers) {
                return false;
            }

            // Personalised payment checks (Only checked if no tiers matched/defined)
            if (this.paymentSettings && this.paymentSettings.enable_personalised_payment) {
                let category = "cash";
                if (methodName === "credit_terms") {
                    category = "credit";
                } else if (methodName === "prepaid_credits") {
                    category = "prepaid";
                }

                if (!this.personalizedPayments || !this.personalizedPayments[category]) return false;
            }

            return true;
        },
    },
});
